﻿import * as layerTypes from '../utils/layerTypes';
import * as paths from '../utils/paths';
import * as eventTypes from '../utils/eventTypes';
import { Cluster, OSM, Vector as VectorSource, WMTS } from 'ol/source';
import { Image as ImageLayer, Vector as VectorLayer, WebGLVector as WebGLLayer } from 'ol/layer';
import TileLayer from 'ol/layer/Tile';
import { addOption, getResolutionForScale } from '../utils/utils';
import { fitFeatures } from '../utils/fitUtils';
import * as formats from '../utils/formats';
import * as defaults from '../utils/defaults';
import ImageWMS from 'ol/source/ImageWMS';
import XGeoJSON from '../format/XGeoJSON';
import { handleGeoserviceWfsUrl, handleGeoserviceGeojsonWfsUrl, handleNativeUrl, handleWfsUrl, handleAnalysisLoader, handleGeoserverVectorTileLayerUrl } from '../utils/utils';
import { xhr } from '../loader/xfeatureloader.js';
import { bbox as bboxStrategy, all as allStrategy } from 'ol/loadingstrategy';
import StyleHandler from './StyleHandler';
import * as styleTypes from '../utils/style/styleTypes';
import * as classification from '../utils/style/classification';
import { createOlStyleFunction } from '../utils/style/canvas/OlStyler';
import * as geomTypes from '../utils/geomTypes';
import WKT from 'ol/format/WKT';
import { transformExtent } from 'ol/proj';
import Heatmap from 'ol/layer/Heatmap';
import VectorImageLayer from 'ol/layer/VectorImage.js';
import { createHotspotLayer } from './HotspotLayer';
import Feature from 'ol/Feature';
import LineString from 'ol/geom/LineString.js';
import Point from 'ol/geom/Point.js';
import ImageTile from 'ol/source/ImageTile.js';
import WMTSCapabilities from 'ol/format/WMTSCapabilities';
import { optionsFromCapabilities } from 'ol/source/WMTS';
import { get as getProjection } from 'ol/proj';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorTileSource from 'ol/source/VectorTile';
import TileGrid from 'ol/tilegrid/TileGrid';
import { getTopLeft, getHeight, approximatelyEquals } from 'ol/extent';
import XMVT from '../format/XMVT';
import XTopoJSON from '../format/XTopoJSON';
import { Style, Text, Fill, Stroke } from 'ol/style';

export default class LayerHandler {
    constructor(mapId, map, layerSettingsHandler, projectionHandler, generalSettingsHandler, gisSettingsHandler, selectionHandler, rootURL, classId, mapControl) {
        this.mapControl = mapControl;
        this.rootURL = rootURL;
        this.map = map;
        this.mapId = mapId;
        this.layerSettingsHandler = layerSettingsHandler;
        this.projectionHandler = projectionHandler;
        this.generalSettingsHandler = generalSettingsHandler;
        this.gisSettingsHandler = gisSettingsHandler;
        this.selectionHandler = selectionHandler;
        this.styleHandler = new StyleHandler(this.rootURL);
        this.selectionHandler.styleHandler = this.styleHandler;
        this.selectionHandler.layerHandler = this;
        this.layerIdx = 0;
        this.layerIdxTemp = 0;
        this.layers = [];
        this.temporaryLayerIdx = 5000;
        this.classId = classId;
        this.tileLoadingEnabled = true;
        this.tileSources = [];
        this.refreshIds = {};
        this._registerEventHandlers();
    }

    refreshLayer(layerId, force) {
        try {
            var layer = this.getLayerById(layerId)[0];
            if (layer.getSource().strategy_ == allStrategy && !force) {
                return;
            }


            if (layer != null && layer.getSource() != null) {
                try {
                    layer.getSource().clear();
                } catch (e) {

                }
                layer.getSource().refresh();
            }
        }
        catch (e) {
        }
    }

    enableTile() {
        this.tileLoadingEnabled = true;
        this.tileSources.forEach(source => {
            source.refresh();
        });
    }

    disableTile() {
        this.tileLoadingEnabled = false;
    }

    init() {
        return new Promise((resolve, reject) => {
            let stylesMap = {};
            stylesMap['sys_' + styleTypes.SYSTEM_STYLE_GEOLOCATION] = 'sys_' + styleTypes.SYSTEM_STYLE_GEOLOCATION;
            stylesMap['sys_' + styleTypes.SYSTEM_STYLE_POINT] = 'sys_' + styleTypes.SYSTEM_STYLE_POINT;
            stylesMap['sys_' + styleTypes.SYSTEM_STYLE_LINE] = 'sys_' + styleTypes.SYSTEM_STYLE_LINE;
            stylesMap['sys_' + styleTypes.SYSTEM_STYLE_POLYGON] = 'sys_' + styleTypes.SYSTEM_STYLE_POLYGON;
            stylesMap['sys_' + styleTypes.SYSTEM_STYLE_ALL] = 'sys_' + styleTypes.SYSTEM_STYLE_ALL;
            stylesMap['sys_' + styleTypes.SYSTEM_STYLE_EDIT_POINT] = 'sys_' + styleTypes.SYSTEM_STYLE_EDIT_POINT;
            stylesMap['sys_' + styleTypes.SYSTEM_STYLE_EDIT_LINE] = 'sys_' + styleTypes.SYSTEM_STYLE_EDIT_LINE;
            stylesMap['sys_' + styleTypes.SYSTEM_STYLE_EDIT_POLYGON] = 'sys_' + styleTypes.SYSTEM_STYLE_EDIT_POLYGON;
            stylesMap['sys_' + styleTypes.SYSTEM_STYLE_EDIT_ALL] = 'sys_' + styleTypes.SYSTEM_STYLE_EDIT_ALL;
            stylesMap['sys_' + styleTypes.SYSTEM_STYLE_DWG] = 'sys_' + styleTypes.SYSTEM_STYLE_DWG;
            stylesMap['sys_' + styleTypes.SYSTEM_STYLE_SHAPE] = 'sys_' + styleTypes.SYSTEM_STYLE_SHAPE;
            this._reduceStyles();

            let styles = Object.keys(stylesMap);
            let stylePromises = [];

            styles.forEach(style => {
                stylePromises.push(this.styleHandler.loadStyle(style));
            });

            let systemStylesPromise = Promise.all(stylePromises);
            if (this.mapControl && this.mapControl._profileLoadStep) {
                systemStylesPromise = this.mapControl._profileLoadStep('systemStyles', systemStylesPromise);
            }

            systemStylesPromise.then(() => {
                let layerPromises = [];
                this._createLayers(this.layerSettingsHandler.layers, layerPromises);
                Promise.all(layerPromises).then(() => {
                    resolve();
                }).catch(err => {
                    reject(err);
                });
            }).catch(err => {
                reject(err);
            });
        });
    }

    _reduceStyles() {
        if (this.layerSettingsHandler.styles != null) {
            let styleIds = Object.keys(this.layerSettingsHandler.styles);

            styleIds.forEach(styleId => {
                if (this.layerSettingsHandler.styles[styleId] != null && this.layerSettingsHandler.styles[styleId].trim() != '') {
                    try {
                        let style = JSON.parse(this.layerSettingsHandler.styles[styleId]);
                        this.styleHandler.applyStyle(styleId, style);
                    } catch (err) {

                    }
                }
            });
        }
    }

    _reduceGeoserviceStyle(layer, styles) {
        if (layer.Geoservice.ServiceType == layerTypes.WFS) {
            let defaultStyle = layer.IdDefaultStyle == null ? 'sys_' + styleTypes.SYSTEM_STYLE_ALL : layer.IdDefaultStyle;

            if (defaultStyle != null && styles[defaultStyle] == null) {
                styles[defaultStyle] = defaultStyle;
            }

            if (layer.IdHighlightStyle != null && styles[layer.IdHighlightStyle] == null) {
                styles[layer.IdHighlightStyle] = layer.IdHighlightStyle;
            }

            if (layer.IdSelectStyle != null && styles[layer.IdSelectStyle] == null) {
                styles[layer.IdSelectStyle] = layer.IdSelectStyle;
            }
        }
    }

    _reduceEntityStyle(layer, styles) {
        if (layer.Interpretation == layerTypes.XP_NATIVE || layer.Interpretation == layerTypes.WFS) {
            let defaultStyle = layer.IdDefaultStyle == null ? 'sys_' + styleTypes.SYSTEM_STYLE_ALL : layer.IdDefaultStyle;

            if (defaultStyle != null && styles[defaultStyle] == null) {
                styles[defaultStyle] = defaultStyle;
            }

            if (layer.IdHighlightStyle != null && styles[layer.IdHighlightStyle] == null) {
                styles[layer.IdHighlightStyle] = layer.IdHighlightStyle;
            }

            if (layer.IdSelectStyle != null && styles[layer.IdSelectStyle] == null) {
                styles[layer.IdSelectStyle] = layer.IdSelectStyle;
            }
        }
    }

    refresh() {
        for (var i = 0; i < this.layers.length; i++) {
            var source = this.layers[i].getSource();
            // allStrategy fara URL = sursa populata manual (layere temporare/analiza), nu are ce refetch-ui
            if (source.strategy_ == allStrategy && source.getUrl() == null) {
                continue;
            }
            try {
                source.clear();
            } catch (err) {

            }
            source.refresh();
        }
    }

    getEntityLayersDescriptors() {
        let map = {};

        this.layers.forEach(mapLayer => {
            let layer = this.layerSettingsHandler.getLayerSettings(mapLayer.get('uid'));

            if (layer.Type == layerTypes.ENTITY && (layer.Interpretation == layerTypes.WFS || layer.Interpretation == layerTypes.VECTOR_TILE || layer.Interpretation == layerTypes.NATIVE_XP)) {
                map[layer.Id] = {
                    Id: layer.Id,
                    Name: layer.Title,
                    Type: layer.Entity.Type,
                    IdRef: layer.Entity.Id
                }
            }
        });

        let keys = Object.keys(map);
        let result = [];
        keys.forEach(key => {
            result.push(map[key]);
        });

        return result;
    }

    handleProjectionChange(initialProjection, targetProjection) {
        this.layers.forEach(layer => {
            let layerId = layer.get("uid");
            let layerSettings = this.layerSettingsHandler.getLayerSettings(layerId);
            let xMin, yMin, xMax, yMax;

            if (layerSettings != null) {
                xMin = layerSettings.XMin;
                yMin = layerSettings.YMin;
                xMax = layerSettings.XMax;
                yMax = layerSettings.YMax;
            }

            let extent;

            if (xMin != null && yMin != null && xMax != null && yMax != null) {
                extent = transformExtent([xMin, yMin, xMax, yMax], initialProjection, targetProjection);
                this.layerSettingsHandler.setLayerExtent(layerId, extent);
            } else {
                extent = this.projectionHandler.getProjExtent(this.generalSettingsHandler.settings.ProjCode);
            }

            layer.setExtent(extent);

            if (layerId == layerTypes.SNAP || layerId == layerTypes.MEASUREMENTS || layerId == layerTypes.REDLINE || layerId == layerTypes.DIMENSIONING) {
                layer.getSource().getFeatures().forEach(feature => {
                    feature.getGeometry().transform(initialProjection, targetProjection);
                });
            }

            this.refreshLayer(layerId);
        });
    }

    getLayerStyle(layerId) {
        let layer = this.getLayerSettings(layerId);

        return this.styleHandler.getStyleById(layer.IdDefaultStyle);
    }

    getLayerByName(name) {
        for (var i = 0; i < this.layers.length; i++) {
            if (this.layers[i].get("name") == name) {
                return this.layers[i];
            }
        }
    }

    getLayerById(id) {
        let result = [];
        for (var i = 0; i < this.layers.length; i++) {
            if (this.layers[i].get("uid") == id) {
                result.push(this.layers[i]);
            }
        }
        return result;
    }

    changeLayerOpacity(layerId, opacity) {
        let layers = this.getLayerById(layerId);

        layers.forEach(layer => {
            layer.setOpacity(opacity);
        });
    }

    changeLayerVisibility(layerId, visible) {
        let layers = this.getLayerById(layerId);

        layers.forEach(layer => {
            layer.setVisible(visible);
        });
    }

    changeLayerClassificationVisibility(layerId, classificationIdx, visible) {
        let layers = this.getLayerById(layerId);

        layers.forEach(layer => {
            this._handleLayerClassificationVisibility(layer, layerId, classificationIdx, visible);
        });
    }

    _handleLayerClassificationVisibility(layer, layerId, classificationIdx, visible) {
        let isFirst = false;
        if (layer.get('currentStyle') == null) {
            let layerSettings = this.layerSettingsHandler.getLayerSettings(layerId);
            let style = this.styleHandler.getCombinedStyle(layerSettings.IdDefaultStyle, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle);
            layer.set('currentStyle', style);
            isFirst = true;
        }

        let currentStyle = layer.get('currentStyle');

        if (currentStyle != null && currentStyle.styleType == 'CUSTOM' && currentStyle.style != null && currentStyle.style.Intervals != null) {
            this._handleCustomLayerClassificationVisibility(layer, currentStyle, classificationIdx, visible);
            return;
        }

        if (currentStyle == null || currentStyle.style == null || currentStyle.style.rules == null) {
            return;
        }

        for (let i = 0; i < currentStyle.style.rules.length; i++) {
            let rule = currentStyle.style.rules[i];

            if (rule.classificationIdx == classificationIdx) {
                if (visible) {
                    let temp = [];

                    if (rule.filter.predicates) {
                        rule.filter.predicates.forEach(predicate => {
                            if (!(predicate.propertyname == 'xstate' && predicate.literal == 4)) {
                                temp.push(predicate);
                            }
                        });
                        rule.filter.predicates = temp;
                    } else {
                        if (rule.filter.propertyname == 'xstate' && rule.filter.oldLiteral != null) {
                            rule.filter.literal = rule.filter.oldLiteral;
                        }
                    }
                } else {
                    if (rule.filter.predicates) {
                        rule.filter.predicates.push({
                            "type": "comparison",
                            "matchcase": true,
                            "operator": "propertyisequalto",
                            "propertyname": "xstate",
                            "literal": 4
                        });
                    } else {
                        if (rule.filter.propertyname == 'xstate') {
                            rule.filter.oldLiteral = rule.filter.literal;
                            rule.filter.literal = 4;
                        }
                    }
                }
            } else if (isFirst && visible) {
                if (rule.filter.predicates) {
                    rule.filter.predicates.push({
                        "type": "comparison",
                        "matchcase": true,
                        "operator": "propertyisequalto",
                        "propertyname": "xstate",
                        "literal": 4
                    });
                } else {
                    if (rule.filter.propertyname == 'xstate') {
                        rule.filter.oldLiteral = rule.filter.literal;
                        rule.filter.literal = 4;
                    }
                }
            }
        }


        layer.set('currentStyle', currentStyle);

        let styleGetter = createOlStyleFunction(currentStyle.style, this.map, this._getRenderSimplificationOptions());
        let styleFunction = (feature) => {
            return styleGetter(feature);
        }
        layer.setStyle(styleFunction)
    }

    _handleCustomLayerClassificationVisibility(layer, currentStyle, classificationIdx, visible) {
        let intervals = currentStyle.style.Intervals;
        let targetIdx = classificationIdx - 1;

        if (targetIdx < 0 || targetIdx >= intervals.length) {
            return;
        }

        intervals[targetIdx].hidden = !visible;
        layer.set('currentStyle', currentStyle);

        if (!window.CustomStyleHandler) {
            return;
        }

        if (this.customStyleHandler == null) {
            this.customStyleHandler = new window.CustomStyleHandler(this.styleHandler);
        }

        layer.setStyle(this.customStyleHandler._buildBinStyleFunction(currentStyle.style));
    }

    changeLayersOrder(layers, fromStart) {
        if (fromStart) {
            this.layerIdxTemp = 0;
        }

        for (let i = layers.length - 1; i >= 0; i--) {
            let layer = layers[i];

            if (layer.Type == layerTypes.GROUP) {
                this.changeLayersOrder(layer.Items);
            } else {
                let mapLayers = this.getLayerById(layer.Id);

                if (mapLayers != null && mapLayers.length > 0) {
                    mapLayers.forEach(mapLayer => {
                        mapLayer.setZIndex(this.layerIdxTemp);
                    })
                    this.layerIdxTemp++;
                }
            }
        }
    }

    removeLayers(ids, changeOrder) {
        if (ids != null && ids.length > 0) {
            for (let i = 0; i < ids.length; i++) {
                let layerId = ids[i];
                let idx = 0;
                let found = false;

                for (let j = 0; j < this.layers.length; j++) {
                    if (this.layers[j].get("uid") == layerId) {
                        found = true;
                        j = this.layers.length;
                    } else {
                        idx++;
                    }
                }

                if (found) {
                    let toRemove = this.layers[idx];
                    this.map.removeLayer(toRemove);
                    this.layers.splice(idx, 1);
                }
            }
        }

        if (changeOrder) {
            this.changeLayersOrder(this.layerSettingsHandler.layers, true);
        }
    }

    addTemporaryVectorLayer(options) {
        let optionsCheck = this._checkTemporaryVector(options);

        if (!optionsCheck.status) {
            return new Promise((resolve, reject) => { resolve(optionsCheck); });
        }

        return this._createTemporaryLayer(options);
    }

    clearSelection() {
        this.selectionHandler.clearSelection();
    }

    fitFeature(layerId, featureId) {
        let layer = this.getLayerById(layerId);

        if (layer.length > 0) {
            if (layer[0].isVisible()) {
                let idField = this.getLayerIdField(layerId);
                let feature = layer[0].getSource().getFeatures().find(x => x.get(idField) == featureId);
                return this._fitFeature(feature, layer[0]);
            } else {
                // TO DO de tradus
                swal.fire({ title: 'Atenție!', html: 'Stratul nu este vizibil în hartă.' })
            }
        }

        return false;
    }

    fitWktGeometry(wkt, scale, featureProjection, dataProjection) {
        let feature = this.projectionHandler.wktToGeometry(wkt, featureProjection, dataProjection);

        return fitFeatures(this.map.getView(), [feature], {
            projCode: this.generalSettingsHandler.settings.ProjCode,
            scale: scale,
            duration: 1500
        });
    }

    getFeatureById(layerId, featureId) {
        let layer = this.getLayerById(layerId);

        let idField = this.getLayerIdField(layerId);
        if (layer.length > 0) {
            if (layer[0].isVisible()) {
                let feature = layer[0].getSource().getFeatures().find(x => x.get(idField) == featureId);
                return feature;
            } else {
                swal.fire({ title: 'Atenție!', html: 'Stratul nu este vizibil în hartă.' })
            }
        }

        return null;
    }

    getLayerSettings(layerId) {
        return this.layerSettingsHandler.getLayerSettings(layerId);
    }

    getLayerIdField(layerId) {
        let layerSettings = this.layerSettingsHandler.getLayerSettings(layerId);

        let idField;

        // Layerele inregistrate doar in harta (temporare) nu au settings, deci nu au nici id field.
        if (layerSettings == null) {
            return null;
        }

        if (layerSettings.Entity != null) {
            // Type 2 = analiza; entitatea ei expune pkField, normalizat in PkFieldName la adaugarea layerului.
            idField = layerSettings.Entity.PkFieldName != null
                ? layerSettings.Entity.PkFieldName
                : layerSettings.Entity.PkField;
        } else if (layerSettings.Geoservice != null) {
            idField = layerSettings.Geoservice.IdColumn;
        } else if (layerSettings.Type == layerTypes.TEMPORARY_VECTOR) {
            idField = layerSettings.idField;
        }

        return idField;
    }

    fitTemporaryFeature(layerId, featureId) {
        let layer = this.getLayerById(layerId);

        if (layer.length > 0) {
            if (layer[0].isVisible()) {
                let feature = layer[0].getSource().getFeatureById(featureId);
                return this._fitFeature(feature, layer[0]);
            } else {
                // TO DO de tradus
                swal.fire({ title: 'Atenție!', html: 'Stratul nu este vizibil în hartă.' })
            }
        }

        return false;
    }

    addTemporaryFeatures(layerId, features, format, clearPrevious, fit) {
        if (format == null) {
            return { status: false, message: 'format is mandatory' };
        }

        if (features == null) {
            return { status: false, message: 'no features were provided' };
        }

        if (layerId == null) {
            return { status: false, message: 'layerId is mandatory' };
        }

        let layer = this.getLayerById(layerId);

        if (layer.length == 0) {
            return { status: false, message: 'layer was not found' };
        }

        let parsedFeatures = this._parseTemporaryFeatures(layer[0], features, format);

        if (clearPrevious) {
            layer[0].getSource().clear();
        }

        if (parsedFeatures.length > 0) {
            layer[0].getSource().addFeatures(parsedFeatures);

            if (fit) {
                this._fitAllLayerFeatures(layer[0]);
            }
        }
    }

    removeTemporaryFeatures(layerId, features) {
        if (layerId == null) {
            return { status: false, message: 'layerId is mandatory' };
        }

        let layer = this.getLayerById(layerId);

        if (layer.length == 0) {
            return { status: false, message: 'layer was not found' };
        }

        let source = layer[0].getSource();

        if (features != null && features.length > 0) {
            features.forEach(feature => {
                let toRemove = source.getFeatureById(feature);

                if (toRemove != null) {
                    source.removeFeatures([toRemove]);
                }
            });
        }
    }

    clearTemporaryFeatures(layerId) {
        if (layerId == null) {
            return { status: false, message: 'layerId is mandatory' };
        }

        let layer = this.getLayerById(layerId);

        if (layer.length == 0) {
            return { status: false, message: 'layer was not found' };
        }

        let source = layer[0].getSource();

        source.clear();
    }

    _registerEventHandlers() {
        $event.register(eventTypes.LAYER_SELECTION_CLEARED, this._getLayerSelectionClearedHandler(), this, this.classId);
        $event.register(eventTypes.FEATURES_MODIFIED, this._getFeaturesModifiedHandler(), this, this.classId);
        $event.register(eventTypes.LAYER_FIT, this._getLayerFitHandler(), this, this.classId);
        $event.register(eventTypes.LEGEND_LAYER_RECREATED, this._getLayerRecreatedHandler(), this, this.classId);
        $event.register(eventTypes.LAYER_ADDED, this._getLayerAddedHandler(), this, this.classId);
        $event.register(eventTypes.CLEAR_TEMPORARY_FEATURES, this._getClearTemporaryFeaturesHandler(), this, this.classId);
    }

    _getFeaturesModifiedHandler() {
        var handler = (eventName, data, sender) => {
            if (data != null && data.oldFeatures != null && data.newFeatures != null && data.layerId != null) {
                this._updateLayerFeatures(data.oldFeatures, data.newFeatures, data.layerId);
            }
        };

        return handler;
    }

    _getLayerSelectionClearedHandler() {
        var handler = (eventName, data, sender) => {
            if (data != null && data.layerId != null) {
                this.selectionHandler.clearLayerSelection(data.layerId);
            }
        };

        return handler;
    }

    // Cand toate feature-urile sunt incarcate client-side (allStrategy - ex. geojson geoservice
    // sau wfs cu allStrategy) nu exista un request de bbox server, deci se fit-uieste direct din sursa.
    _canFitFromSource(layer) {
        let source = layer.getSource != null ? layer.getSource() : null;

        return source != null && source.strategy_ == allStrategy;
    }

    _getLayerFitHandler() {
        var handler = (eventName, data, sender) => {
            if (data != null && data.layerId != null) {
                let layer = this.getLayerById(data.layerId);

                if (layer.length > 0) {
                    if (layer[0].get('temporarySettings') != null || this._canFitFromSource(layer[0])) {
                        this._fitAllLayerFeatures(layer[0]);
                    } else {
                        this.layerSettingsHandler.getLayerBbox(data.layerId, this.map.getView().getProjection().getCode()).then(result => {
                            if (result != null && result.trim() != '') {
                                let format = new WKT();
                                let mapProjection = this.generalSettingsHandler.settings.ProjCode;

                                let feature = format.readFeature(result, {
                                    dataProjection: mapProjection,
                                    featureProjection: mapProjection
                                });

                                this._fitFeature(feature);
                            }
                        });
                    }
                }
            }
        };

        return handler;
    }

    fitLayerExtent(layerId) {
        return new Promise((resolve, reject) => {
            let layer = this.getLayerById(layerId);

            if (layer.length > 0) {
                if (layer[0].get('temporarySettings') != null || this._canFitFromSource(layer[0])) {
                    this._fitAllLayerFeatures(layer[0]);
                    resolve(true);
                } else {
                    this.layerSettingsHandler.getLayerBbox(layerId, this.map.getView().getProjection().getCode()).then(result => {
                        if (result != null && result.trim() != '') {
                            let format = new WKT();
                            let mapProjection = this.generalSettingsHandler.settings.ProjCode;

                            let feature = format.readFeature(result, {
                                dataProjection: mapProjection,
                                featureProjection: mapProjection
                            });

                            this._fitFeature(feature);
                            resolve(true);
                        } else {
                            resolve(false);
                        }
                    });
                }
            }
        });
    }

    _getLayerStyleChangedHandler() {
        var handler = (eventName, data, sender) => {
            if (data != null && data.layerId != null) {
                let layer = this.getLayerById(data.layerId);
                this.removeLayers([data.layerId], false);

                if (layer.length > 0) {
                    let zIndex = layer[0].get('zIndex');
                    this._recreateLayer(data.layerId, zIndex);
                }
            }
        };

        return handler;
    }

    _getRenderSimplificationOptions() {
        const simplificationDefaults = {
            enabled: false,
            toleranceFactor: 2.2,
            bucketStepsPerZoom: 1,
            maxBucketsPerGeometry: 2,
            minVertexCount: 1024,
            avoidComputeWhileInteracting: true,
            reuseNearestBucketWhileInteracting: true
        };
        const cullingDefaults = {
            enabled: true,
            minLineLengthPx: 3.5,
            minPolygonAreaPx2: 10,
            minPolygonExtentPx: 3.75
        };

        let configuredSimplification = {};
        let configuredCulling = {};
        try {
            const rawSimplification = typeof localStorage !== 'undefined'
                ? localStorage.getItem('xportal.renderSimplification')
                : null;
            if (rawSimplification != null && String(rawSimplification).trim() !== '') {
                configuredSimplification = JSON.parse(rawSimplification);
            }
        } catch (e) {
            configuredSimplification = {};
        }

        try {
            const rawCulling = typeof localStorage !== 'undefined'
                ? localStorage.getItem('xportal.renderCulling')
                : null;
            if (rawCulling != null && String(rawCulling).trim() !== '') {
                configuredCulling = JSON.parse(rawCulling);
            }
        } catch (e) {
            configuredCulling = {};
        }

        const simplificationSettings = { ...simplificationDefaults, ...configuredSimplification };
        const cullingSettings = { ...cullingDefaults, ...configuredCulling };

        return {
            renderSimplification: simplificationSettings,
            renderCulling: cullingSettings
        };
    }

    // Raport imagine/viewport pt. VectorImageLayer; default 1.25 (panning mai clar), override din xportal.imageRatio (>=1).
    _getConfiguredImageRatio() {
        const defaultRatio = 1.25;
        try {
            const raw = typeof localStorage !== 'undefined'
                ? localStorage.getItem('xportal.imageRatio')
                : null;
            if (raw != null && String(raw).trim() !== '') {
                const val = parseFloat(raw);
                if (!isNaN(val) && val >= 1) {
                    return val;
                }
            }
        } catch (e) {
            // ignore
        }
        return defaultRatio;
    }

    _themeStyleGetter(feature, style, themeConfig, styleGetter){
        const resolvedStyleGetter = styleGetter || createOlStyleFunction(style, this.map, this._getRenderSimplificationOptions());
        let result = resolvedStyleGetter(feature);

        const themeStyle = themeConfig && themeConfig.style ? themeConfig.style : themeConfig;
        const labelTemplate = themeStyle && themeStyle.LabelTemplate != null
            ? String(themeStyle.LabelTemplate).trim()
            : '';
        const nameEntityField = themeStyle && themeStyle.NameEntityField != null
            ? String(themeStyle.NameEntityField).trim()
            : '';

        let labelValue = null;
        if (labelTemplate !== '') {
            labelValue = this._resolveThemeLabelTemplate(labelTemplate, feature);
        } else if (nameEntityField !== '') {
            labelValue = feature.get(nameEntityField);
        }

        if (labelValue == null || String(labelValue).trim() === '') {
            return result;
        }

        const labelStyle = new Style({
            text: new Text({
                text: String(labelValue),
                font: '14px sans-serif',
                fill: new Fill({ color: '#111111' }),
                stroke: new Stroke({ color: '#ffffff', width: 3 })
            })
        });

        if (!result) {
            return [labelStyle];
        }

        if (Array.isArray(result)) {
            return [...result, labelStyle];
        }

        return [result, labelStyle];
    }

    _resolveThemeLabelTemplate(labelTemplate, feature) {
        return labelTemplate.replace(/\{([^}]+)\}/g, (match, fieldName) => {
            const key = String(fieldName).trim();
            if (!key) {
                return '';
            }
            const value = feature.get(key);
            return value == null ? '' : String(value);
        });
    }

    changeLayerStyle(layerId) {
        return new Promise((resolve, reject) => {
            let layers = this.getLayerById(layerId);

            if (layers.length > 0) {
                let oldLayer = layers[0];
                let zIndex = oldLayer.get('zIndex');
                let layerSettings = this.layerSettingsHandler.getLayerSettings(oldLayer.get('uid'));
                let layerOptions = this._getVectorLayerOptions(layerSettings, zIndex);

                // resetam clasificarea de legenda; doar ramura tematica o recompleteaza dupa _handleTheme
                layerSettings.thematicClassification = null;

                let source;

                if (oldLayer.getSource != null && oldLayer.getSource() != null && oldLayer.getSource().getSource != null && oldLayer.getSource().getSource() != null) {
                    this.removeLayers([layerId], false);
                    return this._recreateLayer(layerId, zIndex).then(() => { resolve() });
                } else if (oldLayer.getSource != null && oldLayer.getSource() != null) {
                    source = oldLayer.getSource();
                } else {
                    resolve();
                }

                this.styleHandler.loadStyle((layerSettings.IdDefaultStyle == null ? 'sys_' + styleTypes.SYSTEM_STYLE_ALL : layerSettings.IdDefaultStyle), (layerSettings.IdDefaultStyle == null ? true : false)).then(() => {
                    this.styleHandler.loadStyle(layerSettings.IdHighlightStyle).then(() => {
                        this.styleHandler.loadStyle(layerSettings.IdSelectStyle).then(() => {
                            let style;

                            if (layerSettings.IdDefaultStyle == null) {
                                // Lipsa stilului default nu inseamna lipsa hover/select: pe sistem cade doar default-ul.
                                style = this.styleHandler.getCombinedStyle('sys_' + styleTypes.SYSTEM_STYLE_ALL, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle);
                            } else {
                                style = this.styleHandler.getCombinedStyle(layerSettings.IdDefaultStyle, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle);
                            }

                            //here

                            if (style.styleType == styleTypes.STYLE_TYPE_CANVAS) {
                                let styleGetter = createOlStyleFunction(style.style, this.map, this._getRenderSimplificationOptions());
                                let styleFunction = (feature) => {
                                    return styleGetter(feature);
                                }

                                addOption(layerOptions, "source", source);
                                addOption(layerOptions, "style", styleFunction);
                                addOption(layerOptions, 'imageRatio', this._getConfiguredImageRatio());

                                this.removeLayers([layerId], false);

                                let layer = new VectorImageLayer(layerOptions);
                                this.layers.push(layer);
                                this.map.addLayer(layer);

                                if (layerSettings.IdHighlightStyle != null) {
                                    this.selectionHandler.computeHover(layer);
                                }

                                if (layerSettings.IdSelectStyle != null) {
                                    this.selectionHandler.computeSelection(layer);
                                }

                                resolve(layer);
                            } else if (style.styleType == styleTypes.STYLE_TYPE_HEATMAP) {
                                addOption(layerOptions, "source", source);
                                addOption(layerOptions, "gradient", style.style.ColorRamp);
                                addOption(layerOptions, "radius", style.style.Radius);
                                addOption(layerOptions, "blur", style.style.Blur);
                                addOption(layerOptions, "weight", this._handleWeightFunction(style.style.IdEntityField, style.style.DefaultWeight));

                                this.removeLayers([layerId], false);

                                let layer = new Heatmap(layerOptions);
                                this.layers.push(layer);
                                this.map.addLayer(layer);
                            } else if (style.styleType == styleTypes.STYLE_TYPE_HOTSPOT) {
                                this.removeLayers([layerId], false);
                                let layer = createHotspotLayer({
                                    source: source,
                                    bandwidth: style.style.Bandwidth,
                                    threshold: style.style.Threshold,
                                    symbolSize: style.style.SymbolSize,
                                    hotColor: style.style.HotColor,
                                    coldColor: style.style.ColdColor,
                                    neutralColor: style.style.NeutralColor,
                                    spatialWeight: style.style.SpatialWeight,
                                    weight: this._handleWeightFunction(style.style.IdEntityField, style.style.DefaultWeight),
                                    zIndex: layerOptions.zIndex,
                                    visible: layerOptions.visible
                                });
                                layer.set('uid', layerSettings.Id);
                                this.layers.push(layer);
                                this.map.addLayer(layer);

                                if (layerSettings.IdHighlightStyle != null) {
                                    this.selectionHandler.computeHover(layer);
                                }

                                if (layerSettings.IdSelectStyle != null) {
                                    this.selectionHandler.computeSelection(layer);
                                }

                                resolve(layer);
                            } else if (style.styleType == styleTypes.STYLE_TYPE_THEMATIC) {
                                this._handleTheme(layerSettings, style, source).then(themeStyle => {
                                    if (themeStyle != null) {
                                        // clasificarea calculata (per-layer) pentru legenda
                                        layerSettings.thematicClassification = themeStyle.classificationIntervals || null;

                                        if (themeStyle.styleType == styleTypes.STYLE_TYPE_CANVAS) {
                                            let themeStyleGetter = createOlStyleFunction(themeStyle.style, this.map, this._getRenderSimplificationOptions());
                                            let styleFunction = (feature) => {
                                                return this._themeStyleGetter(feature, themeStyle.style, style, themeStyleGetter);
                                            }

                                            addOption(layerOptions, "source", source);
                                            addOption(layerOptions, "style", styleFunction);
                                            addOption(layerOptions, 'imageRatio', this._getConfiguredImageRatio());

                                            this.removeLayers([layerId], false);

                                            let layer = new VectorImageLayer(layerOptions);

                                            this.layers.push(layer);
                                            this.map.addLayer(layer);

                                            this._attachThematicRecompute(layer, layerSettings, style, source);

                                            if (layerSettings.IdHighlightStyle != null) {
                                                this.selectionHandler.computeHover(layer);
                                            }

                                            if (layerSettings.IdSelectStyle != null) {
                                                this.selectionHandler.computeSelection(layer);
                                            }
                                            resolve(layer);
                                        } else {
                                            addOption(layerOptions, "source", source);

                                            this.removeLayers([layerId], false);

                                            addOption(layerOptions, 'style', style.style.featureStyle);
                                            addOption(layerOptions, 'className', 'webgllayer');
                                            let layer = new WebGLLayer(layerOptions);
                                            this.layers.push(layer);
                                            this.map.addLayer(layer);

                                            if (layerSettings.IdHighlightStyle != null) {
                                                this.selectionHandler.computeHover(layer);
                                            }

                                            if (layerSettings.IdSelectStyle != null) {
                                                this.selectionHandler.computeSelection(layer);
                                            }
                                            resolve(layer);
                                        }
                                    } else {
                                        resolve();
                                    }
                                });
                            }
                            else if (style.styleType == 'CUSTOM' && window.CustomStyleHandler) {
                                this.removeLayers([layerId], false);
                                return this._recreateLayer(layerId, zIndex).then(() => { resolve() });
                            }
                            else {
                                addOption(layerOptions, "source", source);
                                this.removeLayers([layerId], false);

                                addOption(layerOptions, 'style', style.style.featureStyle);
                                addOption(layerOptions, 'className', 'webgllayer');
                                let layer = new WebGLLayer(layerOptions);
                                this.layers.push(layer);
                                this.map.addLayer(layer);

                                if (layerSettings.IdHighlightStyle != null) {
                                    this.selectionHandler.computeHover(layer);
                                }

                                if (layerSettings.IdSelectStyle != null) {
                                    this.selectionHandler.computeSelection(layer);
                                }

                                if (style.style.isTimeDependant) {
                                    this._handleLayerTimeDependency(layer.get('uid'));
                                }

                                resolve(layer);
                            }
                        });
                    });
                });
            }
        });
    }

    _handleTheme(layerSettings, style, source) {
        return new Promise((resolve, reject) => {
            let fieldName = style.style.NameEntityField;
            let strokeWidth = style.style.StrokeWidth;

            // clasificare categoriala (valori unice): reguli de egalitate
            if ((style.style.Mode || '').toString().toUpperCase() == 'CATEGORIZED') {
                let hasStored = style.style.Intervals != null && style.style.Intervals.some(iv => iv.value != null && iv.value !== '');

                if (hasStored) {
                    resolve(this.styleHandler.getThematicCategorizedStyle(layerSettings.IdDefaultStyle, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle, null, strokeWidth));
                    return;
                }

                // fara categorii salvate => auto din feature-urile in memorie (analize XpQuery / WFS_ALL)
                if (source != null && source.getFeatures().length > 0) {
                    let values = source.getFeatures().map(f => f.get(fieldName));
                    let categories = classification.computeCategories(values, 50).values.map((c, i) => ({ value: c.value, color: classification.paletteColor(i), label: c.value }));

                    if (categories.length > 0) {
                        resolve(this.styleHandler.getThematicCategorizedStyle(layerSettings.IdDefaultStyle, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle, categories, strokeWidth));
                        return;
                    }
                }

                // sursa inca goala la creare (analize/WFS_ALL): stil categorial gol ca layerul sa fie creat;
                // _attachThematicRecompute reaplica clasificarea cand sosesc feature-urile
                resolve(this.styleHandler.getThematicCategorizedStyle(layerSettings.IdDefaultStyle, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle, [], strokeWidth));
                return;
            }

            // valori manuale => folosim intervalele salvate
            if (style.style.AutoValues != true) {
                resolve(this.styleHandler.getThematicCombinedStyle(layerSettings.IdDefaultStyle, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle, null, null, strokeWidth));
                return;
            }

            let method = (style.style.Method || classification.METHOD_EQUAL).toString().toUpperCase();
            let classCount = (style.style.Intervals != null && style.style.Intervals.length > 0) ? style.style.Intervals.length : 5;

            // EQUAL = comportamentul clasic (min/max + impartire egala)
            if (method == classification.METHOD_EQUAL) {
                this._handleThemeEqual(layerSettings, fieldName, strokeWidth, source).then(s => resolve(s));
                return;
            }

            // metode noi (cuantile/jenks/stddev): layerele cu toate feature-urile in memorie
            // (analize XpQuery + WFS_ALL) se clasifica client-side, exact peste datele afisate
            let isVirtual = layerSettings.Entity == null || layerSettings.Entity.Type == 2;
            let isAllLoaded = layerSettings.LoadingStrategy == defaults.WFS_ALL_STRATEGY;
            let hasFeatures = source != null && source.getFeatures().length > 0;

            if ((isVirtual || isAllLoaded) && hasFeatures) {
                let values = source.getFeatures().map(f => f.get(fieldName));
                let breaks = classification.computeBreaks(values, method, classCount);

                if (breaks != null) {
                    resolve(this.styleHandler.getThematicCombinedStyleFromBreaks(layerSettings.IdDefaultStyle, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle, breaks, strokeWidth));
                } else {
                    this._handleThemeEqual(layerSettings, fieldName, strokeWidth, source).then(s => resolve(s));
                }
                return;
            }

            // layere entitate incarcate pe bbox => clasificare server-side pe distributia completa
            // (fallback pe EQUAL cat timp endpointul nu exista / nu raspunde)
            if (layerSettings.Entity != null && layerSettings.Entity.Type != 2) {
                $.ajax({
                    type: "GET",
                    url: this.rootURL + paths.GET_FIELD_CLASSIFICATION.replaceAll('{0}', layerSettings.Entity.Id).replaceAll('{1}', fieldName).replaceAll('{2}', method).replaceAll('{3}', classCount),
                    async: true,
                    contentType: "application/json; charset=utf-8",
                    success: (result) => {
                        if (result != null && result.Breaks != null && result.Breaks.length > 1) {
                            resolve(this.styleHandler.getThematicCombinedStyleFromBreaks(layerSettings.IdDefaultStyle, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle, result.Breaks, strokeWidth));
                        } else {
                            this._handleThemeEqual(layerSettings, fieldName, strokeWidth, source).then(s => resolve(s));
                        }
                    },
                    error: (e) => {
                        this._handleThemeEqual(layerSettings, fieldName, strokeWidth, source).then(s => resolve(s));
                    }
                });
                return;
            }

            // fara date si fara sursa server => cadem pe EQUAL
            this._handleThemeEqual(layerSettings, fieldName, strokeWidth, source).then(s => resolve(s));
        });
    }

    // clasificarea clasica EQUAL: min/max (scanare feature-uri WFS_ALL sau range server) + impartire egala
    _handleThemeEqual(layerSettings, fieldName, strokeWidth, source) {
        return new Promise((resolve) => {
            let isEntity = layerSettings.Entity != null && layerSettings.Entity.Type != 2;

            // WFS_ALL cu feature-uri deja in memorie => scanam min/max direct
            if (layerSettings.LoadingStrategy == defaults.WFS_ALL_STRATEGY && source != null && source.getFeatures().length > 0) {
                let min = Infinity;
                let max = -Infinity;

                source.getFeatures().forEach(feature => {
                    let value = feature.get(fieldName);

                    if (value != null) {
                        if (value > max) { max = value; }
                        if (value < min) { min = value; }
                    }
                });

                if (min == Infinity || max == -Infinity) {
                    resolve(null);
                    return;
                }

                resolve(this.styleHandler.getThematicCombinedStyle(layerSettings.IdDefaultStyle, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle, min, max, strokeWidth));
                return;
            }

            // entitate (inclusiv WFS_ALL inca neincarcat la creare) => range de pe server; nu depinde de feature-urile incarcate
            if (isEntity) {
                $.ajax({
                    type: "GET",
                    url: this.rootURL + paths.GET_FIELD_RANGE.replaceAll('{0}', layerSettings.Entity.Id).replaceAll('{1}', fieldName),
                    async: true,
                    contentType: "application/json; charset=utf-8",
                    success: (result) => {
                        let min = 0;
                        let max = 10000;

                        if (result != null) {
                            min = result.MinValue;
                            max = result.MaxValue;
                        }

                        resolve(this.styleHandler.getThematicCombinedStyle(layerSettings.IdDefaultStyle, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle, min, max, strokeWidth));
                    },
                    error: (e) => {
                        resolve(null);
                    }
                });
                return;
            }

            // layer virtual (analiza) fara feature-uri inca => fallback dummy;
            // fara clasificare in legenda (o completeaza _attachThematicRecompute cand sosesc feature-urile)
            let dummyStyle = this.styleHandler.getThematicCombinedStyle(layerSettings.IdDefaultStyle, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle, 0, 10000, strokeWidth);

            if (dummyStyle != null) {
                dummyStyle.classificationIntervals = null;
            }

            resolve(dummyStyle);
        });
    }

    // clasificarea din feature-urile in memorie (analize Type==2 / WFS_ALL) are sursa goala la creare;
    // reaplicam stilul o singura data cand se incarca feature-urile
    _attachThematicRecompute(layer, layerSettings, styleDescriptor, source) {
        let isVirtual = layerSettings.Entity == null || layerSettings.Entity.Type == 2;
        let isAllLoaded = layerSettings.LoadingStrategy == defaults.WFS_ALL_STRATEGY;

        if (layer == null || source == null || !(isVirtual || isAllLoaded)) {
            return;
        }

        // schimbari repetate de stil pe aceeasi sursa inca goala ar stivui listeneri => scoatem-l pe cel anterior
        if (layerSettings._thematicRecompute != null) {
            layerSettings._thematicRecompute.source.un('change', layerSettings._thematicRecompute.fn);
            layerSettings._thematicRecompute = null;
        }

        let recompute = () => {
            if (source.getFeatures().length === 0) {
                return;
            }

            source.un('change', recompute);
            layerSettings._thematicRecompute = null;

            this._handleTheme(layerSettings, styleDescriptor, source).then(resolved => {
                if (resolved == null) {
                    return;
                }

                // feature-urile s-au incarcat => clasificarea reala; actualizam legenda
                layerSettings.thematicClassification = resolved.classificationIntervals || null;

                if (resolved.styleType == styleTypes.STYLE_TYPE_CANVAS) {
                    let getter = createOlStyleFunction(resolved.style, this.map, this._getRenderSimplificationOptions());
                    layer.setStyle((feature) => this._themeStyleGetter(feature, resolved.style, styleDescriptor, getter));
                } else if (resolved.style != null && resolved.style.featureStyle != null) {
                    layer.setStyle(resolved.style.featureStyle);
                }

                $event.notify(eventTypes.THEMATIC_CLASSIFICATION_UPDATED, { layerId: layerSettings.Id }, this, this.classId);
            });
        };

        layerSettings._thematicRecompute = { source: source, fn: recompute };
        source.on('change', recompute);
    }

    _getVectorLayerOptions(layerSettings, idx) {
        switch (layerSettings.Type) {
            case layerTypes.GEOSERVICE: return this._getGeoserviceLayerOptions(layerSettings, idx);
            case layerTypes.ENTITY: return this._getEntityLayerOptions(layerSettings, idx);
            default: return null;
        }
    }

    _getGeoserviceLayerOptions(layerSettings, idx) {
        switch (layerSettings.Geoservice.ServiceType) {
            case layerTypes.WFS: return this._getGeoserviceWfsLayerOptions(layerSettings, idx);
            case layerTypes.GEOJSON: return this._getGeoserviceWfsLayerOptions(layerSettings, idx);
            default: return null;
        }
    }

    _getEntityLayerOptions(layerSettings, idx) {
        switch (layerSettings.Interpretation) {
            case layerTypes.XP_NATIVE: {
                if (layerSettings.Entity.Type == 2) {
                    return this._getAnalysisLayerOptions(layerSettings, idx);
                } else {
                    return this._getNativeLayerOptions(layerSettings, idx);
                }
            }
            case layerTypes.WFS: return this._getWfsLayerOptions(layerSettings, idx, false);
            case layerTypes.VECTOR_TILE: return this._getVectorTileLayerOptions(layerSettings, idx, false);
            default: return null;
        }
    }

    _getGeoserviceWfsLayerOptions(layerSettings, idx) {
        var layerOptions = {};

        addOption(layerOptions, "uid", layerSettings.Id);

        addOption(layerOptions, 'visible', layerSettings.VisibleByDefault);
        addOption(layerOptions, 'extent', this._getLayerExtent(layerSettings));
        addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, layerSettings.Geoservice.TileMatrix));
        addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, layerSettings.Geoservice.TileMatrix));
        addOption(layerOptions, "declutter", layerSettings.Declutter);
        addOption(layerOptions, "zIndex", idx);
        addOption(layerOptions, "interpretation", layerTypes.WFS);
        addOption(layerOptions, 'idField', layerSettings.Geoservice.IdColumn);

        return layerOptions;
    }

    _getAnalysisLayerOptions(layerSettings, idx) {
        var layerOptions = {};

        addOption(layerOptions, "uid", layerSettings.Id);

        addOption(layerOptions, 'visible', layerSettings.VisibleByDefault);
        addOption(layerOptions, 'extent', this._getLayerExtent(layerSettings));
        addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, layerSettings.Entity.GeometrySrid));
        addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, layerSettings.Entity.GeometrySrid));
        addOption(layerOptions, "declutter", layerSettings.Declutter);
        addOption(layerOptions, "zIndex", idx);
        addOption(layerOptions, "interpretation", layerTypes.WFS);
        addOption(layerOptions, 'idField', layerSettings.Entity.PkFieldName);

        return layerOptions;
    }

    _getNativeLayerOptions(layerSettings, idx) {
        var layerOptions = {};

        addOption(layerOptions, "uid", layerSettings.Id);
        addOption(layerOptions, 'visible', layerSettings.VisibleByDefault);
        addOption(layerOptions, 'extent', this._getLayerExtent(layerSettings));
        addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, layerSettings.Entity.GeometrySrid));
        addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, layerSettings.Entity.GeometrySrid));
        addOption(layerOptions, "declutter", layerSettings.Declutter);
        addOption(layerOptions, "zIndex", idx);
        addOption(layerOptions, "interpretation", layerTypes.WFS);
        addOption(layerOptions, 'idField', layerSettings.Entity.PkFieldName);

        return layerOptions;
    }

    _getVectorTileLayerOptions(layerSettings, idx) {
        var layerOptions = {};

        addOption(layerOptions, "uid", layerSettings.Id);

        addOption(layerOptions, 'visible', layerSettings.VisibleByDefault);
        addOption(layerOptions, 'extent', this._getLayerExtent(layerSettings));
        addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, layerSettings.Entity.GeometrySrid));
        addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, layerSettings.Entity.GeometrySrid));
        addOption(layerOptions, "declutter", layerSettings.Declutter);
        addOption(layerOptions, "zIndex", idx);
        addOption(layerOptions, "interpretation", layerTypes.WFS);
        addOption(layerOptions, 'idField', layerSettings.Entity.PkFieldName);

        return layerOptions;
    }

    _getWfsLayerOptions(layerSettings, idx) {
        var layerOptions = {};

        addOption(layerOptions, "uid", layerSettings.Id);

        addOption(layerOptions, 'visible', layerSettings.VisibleByDefault);
        addOption(layerOptions, 'extent', this._getLayerExtent(layerSettings));
        addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, layerSettings.Entity.GeometrySrid));
        addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, layerSettings.Entity.GeometrySrid));
        addOption(layerOptions, "declutter", layerSettings.Declutter);
        addOption(layerOptions, "zIndex", idx);
        addOption(layerOptions, "interpretation", layerTypes.WFS);
        addOption(layerOptions, 'idField', layerSettings.Entity.PkFieldName);

        return layerOptions;
    }

    _getLayerStyleModifiedHandler() {
        var handler = (eventName, data, sender) => {
            if (data != null && data.layerId != null) {
                let layer = this.getLayerById(data.layerId);
                this.removeLayers([data.layerId], false);

                if (layer.length > 0) {
                    let zIndex = layer[0].get('zIndex');
                    this._recreateLayer(data.layerId, zIndex);
                }
            }
        };

        return handler;
    }

    _getClearTemporaryFeaturesHandler() {
        var handler = (eventName, data, sender) => {
            if (data != null && data.layerId != null) {
                this.clearTemporaryFeatures(data.layerId);
            }
        };

        return handler;
    }

    _getLayerAddedHandler() {
        var handler = (eventName, data, sender) => {
            if (data != null && data.layerId != null) {
                this.layerIdx++;
                showLoadingPanel();
                this._recreateLayer(data.layerId, this.layerIdx).then(() => {
                    this.changeLayersOrder(this.layerSettingsHandler.layers, true);
                    hideLoadingPanel();
                });
            }
        };

        return handler;
    }

    _getLayerRecreatedHandler() {
        var handler = (eventName, data, sender) => {
            if (data != null && data.layerId != null) {
                let layer = this.getLayerById(data.layerId);
                this.removeLayers([data.layerId], false);

                if (layer.length > 0) {
                    let zIndex = layer[0].get('zIndex');
                    this._recreateLayer(data.layerId, zIndex);
                }
            }
        };

        return handler;
    }

    _recreateLayer(layer, zIndex) {
        if (this.refreshIds[layer]) {
            clearInterval(this.refreshIds[layer]);
            delete this.refreshIds[layer];
        }
        let layerSetting = this.layerSettingsHandler.getLayerSettings(layer);
        return this._createLayer(layerSetting, zIndex).then(() => { });
    }

    _updateLayerFeatures(oldFeatures, newFeatures, layerId) {
        newFeatures.forEach(newFeature => {
            newFeature.layerId = layerId;
            newFeature.set('xstate', styleTypes.DEFAULT_STYLE_TYPE);
        });

        let layer = this.getLayerById(layerId);

        if (layer.length > 0) {
            layer[0].getSource().removeFeatures(oldFeatures);
            layer[0].getSource().addFeatures(newFeatures);

            if (layer[0].get('temporarySettings') != null && layer[0].get('temporarySettings').featureEditCallback != null) {
                let layerSettings = layer[0].get('temporarySettings')
                let format = new WKT();
                let mapProjection = this.generalSettingsHandler.settings.ProjCode;

                let result = [];

                newFeatures.forEach(newFeature => {
                    var feature = format.writeFeature(newFeature, {
                        dataProjection: layerSettings.srid,
                        featureProjection: mapProjection
                    });

                    result.push(feature);
                });

                layer[0].get('temporarySettings').featureEditCallback(result, layerSettings.uid);
            }
        }
    }

    _fitFeature(feature, layer) {
        if (feature == null) {
            return false;
        }

        return fitFeatures(this.map.getView(), [feature], {
            projCode: this.generalSettingsHandler.settings.ProjCode,
            minScale: this._getLayerMinFitScale(layer),
            layer: layer,
            duration: 1500
        });
    }

    _fitAllLayerFeatures(layer) {
        return fitFeatures(this.map.getView(), layer.getSource().getFeatures(), {
            projCode: this.generalSettingsHandler.settings.ProjCode,
            minScale: this._getLayerMinFitScale(layer),
            layer: layer,
            duration: 1500
        });
    }

    // Layerele temporare isi impun scara de fit; pentru restul o deduce fitUtils din tipul geometriei.
    _getLayerMinFitScale(layer) {
        if (layer != null && layer.get('temporarySettings') != null) {
            return layer.get('temporarySettings').minFitScale;
        }

        return null;
    }

    _parseTemporaryFeatures(layer, features, format) {
        let layerSettings = layer.get('temporarySettings');

        switch (format) {
            case formats.WKT: return this._parseWktFeatures(features, layerSettings.geomField, layerSettings.idField, layerSettings.srid, layerSettings.name);
            default: return [];
        }
    }

    _parseWktFeatures(features, geomField, idField, srid, layerId) {
        let format = new WKT();
        let mapProjection = this.generalSettingsHandler.settings.ProjCode;
        let result = [];

        for (let i = 0; i < features.length; i++) {
            if (features[i][geomField] != null && features[i][geomField].trim() != '') {
                var feature = format.readFeature(features[i][geomField], {
                    dataProjection: srid,
                    featureProjection: mapProjection
                });

                features[i].xstate = styleTypes.DEFAULT_STYLE_TYPE;
                feature.setId(features[i][idField]);
                feature.setProperties(features[i]);
                feature.layerId = layerId;
                result.push(feature);
            }
        }

        return result;
    }

    _createLayers(layers, layerPromises) {
        let mapFilters = localStorage.getItem('mapFilters');

        if (mapFilters == null) {
            mapFilters = null;
        } else {
            mapFilters = JSON.parse(mapFilters);
        }

        for (let i = layers.length - 1; i >= 0; i--) {
            let layer = layers[i];

            if (mapFilters != null && mapFilters[layer.Id] != null) {
                layer.Filtered = true;
                layer.Filters = mapFilters[layer.Id];
                layer.WfsFilters = mapFilters[layer.Id + '_wfs'];
                layer.GeojsonFilterTokens = mapFilters[layer.Id + '_geojson'];
            }

            if (layer.Type == layerTypes.GROUP) {
                this._createLayers(layer.Items, layerPromises);
            } else {
                let layerPromise = this._createLayer(layer, this.layerIdx);
                if (this.mapControl && this.mapControl._profileLoadStep) {
                    layerPromise = this.mapControl._profileLoadStep('layer: ' + (layer.Name || layer.Id), layerPromise);
                }
                layerPromises.push(layerPromise);
                this.layerIdx++;
            }
        }
    }

    _createLayer(layer, idx) {
        if (layer == null) {
            return new Promise((resolve, reject) => { resolve(); });
        }

        switch (layer.Type) {
            case layerTypes.GEOSERVICE: return this._createGeoserviceLayer(layer, idx);
            case layerTypes.ENTITY: return this._handleEntityLayer(layer, idx);
            case layerTypes.GRATICULE: return this._createGraticuleLayerFromConfig(layer, idx);
            default: return new Promise((resolve, reject) => { resolve(); });
        }
    }

    _handleEntityLayer(layer, idx) {
        if (layer.url != null) {
            return this._createNativeLayer(layer, idx);
        }

        switch (layer.Interpretation) {
            case layerTypes.XP_NATIVE: {
                if (layer.Entity.Type == 2) {
                    return this._createAnalysisLayer(layer, idx);
                } else {
                    return this._createNativeLayer(layer, idx);
                }
            }
            case layerTypes.WFS: return this._createWfsLayer(layer, idx, false);
            case layerTypes.VECTOR_TILE: return this._createVectorTileLayer(layer, idx, false);
            default: return new Promise((resolve, reject) => { resolve(); });
        }
    }

    _createAnalysisLayer(layerSettings, idx) {
        return new Promise((resolve, reject) => {
            const geomField = layerSettings.Entity.outputValues.find(x => { return x.outputName == layerSettings.Entity.geomField });
            let srid;
            if (geomField == null) {
                srid = this.mapControl.map.getView().getProjection().getCode();
            }
            else {
                srid = geomField.Srid;
            }
            
            this.projectionHandler.load(srid).then(() => {
                this.styleHandler.loadStyle((layerSettings.IdDefaultStyle == null ? 'sys_' + styleTypes.SYSTEM_STYLE_ALL : layerSettings.IdDefaultStyle), (layerSettings.IdDefaultStyle == null ? true : false)).then(() => {
                    this.styleHandler.loadStyle(layerSettings.IdHighlightStyle).then(() => {
                        this.styleHandler.loadStyle(layerSettings.IdSelectStyle).then(() => {
                            var sourceOptions = {};

                            let loadingStrategy = layerSettings.LoadingStrategy == null || layerSettings.LoadingStrategy == '' ? defaults.WFS_DEFAULT_LOADING_STRATEGY : layerSettings.LoadingStrategy;

                            switch (loadingStrategy) {
                                case 'bboxStrategy':
                                    addOption(sourceOptions, 'strategy', bboxStrategy);
                                    break;
                                case "allStrategy":
                                    addOption(sourceOptions, 'strategy', allStrategy);
                                    break;
                            }

                            var source = new VectorSource(sourceOptions);
                            source.setLoader(handleAnalysisLoader(layerSettings, this.map, this.rootURL, source, this.selectionHandler, this.mapControl.generalActionHandler));

                            var layerOptions = {};

                            addOption(layerOptions, "uid", layerSettings.Id);
                            addOption(layerOptions, "source", source);

                            addOption(layerOptions, 'visible', layerSettings.VisibleByDefault);
                            addOption(layerOptions, 'extent', this._getLayerExtent(layerSettings));
                            addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, srid));
                            addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, srid));
                            addOption(layerOptions, "declutter", layerSettings.Declutter);
                            addOption(layerOptions, "zIndex", idx);
                            addOption(layerOptions, "interpretation", layerTypes.WFS);
                            addOption(layerOptions, 'idField', layerSettings.Entity.pkField);

                            this._createVectorLayer(layerSettings, layerOptions, sourceOptions).then((created) => { resolve(); });
                        });
                    });
                });
            });
        });
    }

    _createVectorTileLayer(layerSettings, idx) {
        return new Promise((resolve, reject) => {
            this.projectionHandler.load(layerSettings.Entity.GeometrySrid).then(() => {
                this.styleHandler.loadStyle((layerSettings.IdDefaultStyle == null ? 'sys_' + styleTypes.SYSTEM_STYLE_ALL : layerSettings.IdDefaultStyle), (layerSettings.IdDefaultStyle == null ? true : false)).then(() => {
                    this.styleHandler.loadStyle(layerSettings.IdHighlightStyle).then(() => {
                        this.styleHandler.loadStyle(layerSettings.IdSelectStyle).then(() => {
                            var sourceOptions = {};

                            var projection = getProjection(this.generalSettingsHandler.settings.ProjCode);
                            addOption(sourceOptions, 'tileGrid', this._getTileGrid(this.generalSettingsHandler.settings.ProjCode));
                            addOption(sourceOptions, 'url', handleGeoserverVectorTileLayerUrl(this.mapId, this.rootURL, layerSettings, this.map, this.gisSettingsHandler));
                            addOption(sourceOptions, 'projection', projection);

                            var format = layerSettings.Format == null ? this.gisSettingsHandler.settings.VectorTile_Format : layerSettings.Format;

                            var formatOptions = {};

                            formatOptions.layerId = layerSettings.Id;
                            formatOptions.selectionHandler = this.selectionHandler;
                            formatOptions.generalActionHandler = this.mapControl.generalActionHandler;
                            formatOptions.layerSettings = layerSettings;

                            if (layerSettings.Entity.GeometryFieldName != null && layerSettings.Entity.GeometryFieldName != '') {
                                formatOptions.geometryName = layerSettings.Entity.GeometryFieldName;
                            }

                            formatOptions.dataProjection = projection;
                            formatOptions.featureProjection = projection;

                            switch (format) {
                                case 'application/vnd.mapbox-vector-tile':
                                    addOption(sourceOptions, 'format', new XMVT(formatOptions));
                                    break;
                                case 'application/json;type=geojson':
                                    addOption(sourceOptions, 'format', new XGeoJSON(formatOptions));
                                    break;
                                case 'application/json;type=topojson':
                                    addOption(sourceOptions, 'format', new XTopoJSON(formatOptions));
                                    break;
                                default:
                                    addOption(sourceOptions, 'format', new XMVT(formatOptions));
                                    break;
                            }

                            var source = new VectorTileSource(sourceOptions);

                            var layerOptions = {};

                            addOption(layerOptions, "uid", layerSettings.Id);
                            addOption(layerOptions, "source", source);

                            addOption(layerOptions, 'visible', layerSettings.VisibleByDefault);
                            addOption(layerOptions, 'extent', this._getLayerExtent(layerSettings));
                            addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, layerSettings.Entity.GeometrySrid));
                            addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, layerSettings.Entity.GeometrySrid));
                            addOption(layerOptions, "declutter", layerSettings.Declutter);
                            addOption(layerOptions, "zIndex", idx);
                            addOption(layerOptions, "interpretation", layerTypes.WFS);
                            addOption(layerOptions, 'idField', layerSettings.Entity.PkFieldName);

                            let style;

                            if (layerSettings.IdDefaultStyle == null) {
                                // Lipsa stilului default nu inseamna lipsa hover/select: pe sistem cade doar default-ul.
                                style = this.styleHandler.getCombinedStyle('sys_' + styleTypes.SYSTEM_STYLE_ALL, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle);
                            } else {
                                style = this.styleHandler.getCombinedStyle(layerSettings.IdDefaultStyle, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle);
                            }

                            let styleGetter = createOlStyleFunction(style.style, this.map, this._getRenderSimplificationOptions());
                            let styleFunction = (feature) => {
                                return styleGetter(feature);
                            }

                            addOption(layerOptions, "style", styleFunction);

                            let layer = new VectorTileLayer(layerOptions);
                            this.layers.push(layer);
                            this.map.addLayer(layer);

                            if (layerSettings.IdHighlightStyle != null) {
                                this.selectionHandler.computeHover(layer);
                            }

                            if (layerSettings.IdSelectStyle != null) {
                                this.selectionHandler.computeSelection(layer);
                            }

                            resolve(layer);
                        });
                    });
                });
            });
        });
    }

    _getTileGrid(projId) {
        var projSettings = this.projectionHandler.projections[projId];

        if (projSettings == null || projSettings.GridMatrixLevels == null || projSettings.GridMatrixLevels == 0) {
            return null;
        }

        var fullProjection = getProjection(projId);
        var projectionExtent = fullProjection.getExtent();
        var size = getHeight(projectionExtent) / this.gisSettingsHandler.settings.TileSize;
        var resolutions = new Array(projSettings.GridMatrixLevels);
        for (var z = 0; z < projSettings.GridMatrixLevels; z++) {
            resolutions[z] = size / Math.pow(2, z);
        }

        var tileOptions = {};
        addOption(tileOptions, "extent", projectionExtent);
        addOption(tileOptions, "origin", getTopLeft(projectionExtent));
        addOption(tileOptions, "resolutions", resolutions);
        addOption(tileOptions, "tileSize", [this.gisSettingsHandler.settings.TileSize, this.gisSettingsHandler.settings.TileSize]);

        return new TileGrid(tileOptions);
    }

    _createWfsLayer(layerSettings, idx) {
        return new Promise((resolve, reject) => {
            this.projectionHandler.load(layerSettings.Entity.GeometrySrid).then(() => {
                this.styleHandler.loadStyle((layerSettings.IdDefaultStyle == null ? 'sys_' + styleTypes.SYSTEM_STYLE_ALL : layerSettings.IdDefaultStyle), (layerSettings.IdDefaultStyle == null ? true : false)).then(() => {
                    this.styleHandler.loadStyle(layerSettings.IdHighlightStyle).then(() => {
                        this.styleHandler.loadStyle(layerSettings.IdSelectStyle).then(() => {
                            var sourceOptions = {};

                            var formatOptions = {};

                            formatOptions.layerId = layerSettings.Id;
                            formatOptions.selectionHandler = this.selectionHandler;
                            formatOptions.generalActionHandler = this.mapControl.generalActionHandler;
                            formatOptions.layerSettings = layerSettings;

                            if (layerSettings.Entity.GeometryFieldName != null && layerSettings.Entity.GeometryFieldName != '') {
                                formatOptions.geometryName = layerSettings.Entity.GeometryFieldName;
                            }

                            var geoJsonFormat = new XGeoJSON(formatOptions);

                            addOption(sourceOptions, 'format', geoJsonFormat);
                            addOption(sourceOptions, 'url', handleWfsUrl(this.mapId, this.rootURL, layerSettings, this.map, this.gisSettingsHandler));

                            addOption(sourceOptions, 'loader', xhr(sourceOptions['url'], sourceOptions['format']));

                            let loadingStrategy = layerSettings.LoadingStrategy == null || layerSettings.LoadingStrategy == '' ? defaults.WFS_DEFAULT_LOADING_STRATEGY : layerSettings.LoadingStrategy;

                            switch (loadingStrategy) {
                                case 'bboxStrategy':
                                    addOption(sourceOptions, 'strategy', bboxStrategy);
                                    break;
                                case "allStrategy":
                                    addOption(sourceOptions, 'strategy', allStrategy);
                                    break;
                            }
                            var source = new VectorSource(sourceOptions);


                            var layerOptions = {};

                            addOption(layerOptions, "uid", layerSettings.Id);
                            addOption(layerOptions, "source", source);

                            addOption(layerOptions, 'visible', layerSettings.VisibleByDefault);
                            addOption(layerOptions, 'extent', this._getLayerExtent(layerSettings));
                            addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, layerSettings.Entity.GeometrySrid));
                            addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, layerSettings.Entity.GeometrySrid));
                            addOption(layerOptions, "declutter", layerSettings.Declutter);
                            addOption(layerOptions, "zIndex", idx);
                            addOption(layerOptions, "interpretation", layerTypes.WFS);
                            addOption(layerOptions, 'idField', layerSettings.Entity.PkFieldName);

                            this._createVectorLayer(layerSettings, layerOptions, sourceOptions).then(() => { resolve(); });
                        });
                    });
                });
            });
        });
    }

    _createNativeLayer(layerSettings, idx) {
        return new Promise((resolve, reject) => {
            this.projectionHandler.load(layerSettings.Entity.GeometrySrid).then(() => {
                this.styleHandler.loadStyle((layerSettings.IdDefaultStyle == null ? 'sys_' + styleTypes.SYSTEM_STYLE_ALL : layerSettings.IdDefaultStyle), (layerSettings.IdDefaultStyle == null ? true : false)).then(() => {
                    this.styleHandler.loadStyle(layerSettings.IdHighlightStyle).then(() => {
                        this.styleHandler.loadStyle(layerSettings.IdSelectStyle).then(() => {
                            var sourceOptions = {};

                            var formatOptions = {};

                            formatOptions.layerId = layerSettings.Id;
                            formatOptions.selectionHandler = this.selectionHandler;
                            formatOptions.generalActionHandler = this.mapControl.generalActionHandler;
                            formatOptions.layerSettings = layerSettings;

                            if (layerSettings.Entity.GeometryFieldName != null && layerSettings.Entity.GeometryFieldName != '') {
                                formatOptions.geometryName = layerSettings.Entity.GeometryFieldName;
                            }

                            var geoJsonFormat = new XGeoJSON(formatOptions);

                            addOption(sourceOptions, 'format', geoJsonFormat);
                            addOption(sourceOptions, 'url', handleNativeUrl(this.mapId, layerSettings, this.map, this.rootURL));

                            addOption(sourceOptions, 'loader', xhr(sourceOptions['url'], sourceOptions['format']));

                            let loadingStrategy = layerSettings.LoadingStrategy == null || layerSettings.LoadingStrategy == '' ? defaults.WFS_DEFAULT_LOADING_STRATEGY : layerSettings.LoadingStrategy;

                            switch (loadingStrategy) {
                                case 'bboxStrategy':
                                    addOption(sourceOptions, 'strategy', bboxStrategy);
                                    break;
                                case "allStrategy":
                                    addOption(sourceOptions, 'strategy', allStrategy);
                                    break;
                            }
                            var source = new VectorSource(sourceOptions);


                            var layerOptions = {};

                            addOption(layerOptions, "uid", layerSettings.Id);
                            addOption(layerOptions, "source", source);

                            addOption(layerOptions, 'visible', layerSettings.VisibleByDefault);
                            addOption(layerOptions, 'extent', this._getLayerExtent(layerSettings));
                            addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, layerSettings.Entity.GeometrySrid));
                            addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, layerSettings.Entity.GeometrySrid));
                            addOption(layerOptions, "declutter", layerSettings.Declutter);
                            addOption(layerOptions, "zIndex", idx);
                            addOption(layerOptions, "interpretation", layerTypes.WFS);
                            addOption(layerOptions, 'idField', layerSettings.Entity.PkFieldName);

                            this._createVectorLayer(layerSettings, layerOptions, sourceOptions).then(() => { resolve(); });
                        });
                    });
                });
            });
        });
    }

    _createVectorLayer(layerSettings, layerOptions, sourceOptions) {
        return new Promise((resolve, reject) => {
            let style;

            if (layerSettings.IdDefaultStyle == null) {
                // Lipsa stilului default nu inseamna lipsa hover/select: pe sistem cade doar default-ul.
                style = this.styleHandler.getCombinedStyle('sys_' + styleTypes.SYSTEM_STYLE_ALL, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle);
            } else {
                style = this.styleHandler.getCombinedStyle(layerSettings.IdDefaultStyle, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle);
            }

            let layer;

            if (layerSettings.RefreshTime != null && !isNaN(layerSettings.RefreshTime) && parseInt(layerSettings.RefreshTime.toString()) > 0) {

                const layerId = layerSettings.Id;
                const time = parseInt(layerSettings.RefreshTime.toString()) * 1000;
                let intervalId = setInterval(() => {
                    this.refreshLayer(layerId, true);
                }, time);

                this.refreshIds[layerId] = intervalId;
            }

            if (style.styleType == styleTypes.STYLE_TYPE_CANVAS) {
                let styleGetter = createOlStyleFunction(style.style, this.map, this._getRenderSimplificationOptions());
                let styleFunction = (feature) => {
                    return styleGetter(feature);
                }

                addOption(layerOptions, "style", styleFunction);
                addOption(layerOptions, 'imageRatio', this._getConfiguredImageRatio());

                layer = new VectorImageLayer(layerOptions);
                this.layers.push(layer);
                this.map.addLayer(layer);

                if (layerSettings.IdHighlightStyle != null) {
                    this.selectionHandler.computeHover(layer);
                }

                if (layerSettings.IdSelectStyle != null) {
                    this.selectionHandler.computeSelection(layer);
                }

                resolve(layer);
            } else if (style.styleType == styleTypes.STYLE_TYPE_CLUSTER) {
                let styleGetter = createOlStyleFunction(style.style.style, this.map, this._getRenderSimplificationOptions());
                let styleFunction = (feature) => {
                    return styleGetter(feature);
                }

                addOption(layerOptions, "style", styleFunction);

                var source = layerOptions.source;

                var clusterOptions = {};
                addOption(clusterOptions, 'source', source);
                addOption(clusterOptions, 'distance', 50);

                var clusterSource = new Cluster(clusterOptions);

                clusterSource.on('addfeature', (evt) => {
                    if (evt && evt.feature){
                        evt.feature.set('xstate', 0);
                    }

                    let clusterFeatures = evt.feature.get('features');

                    if (clusterFeatures != null && clusterFeatures.length == 1) {
                        if (this.selectionHandler.selectionHolder.layers[layerSettings.Id] != null) {
                            let selectionLayer = this.selectionHandler.selectionHolder.layers[layerSettings.Id];

                            if (selectionLayer.features[clusterFeatures[0].get(selectionLayer.idField)] != null) {
                                evt.feature.set('xstate', 2);
                            }
                        }
                    }
                });

                layerOptions.source = clusterSource;

                layer = new VectorLayer(layerOptions);
                this.layers.push(layer);
                this.map.addLayer(layer);

                if (layerSettings.IdHighlightStyle != null) {
                    this.selectionHandler.computeHover(layer);
                }

                if (layerSettings.IdSelectStyle != null) {
                    this.selectionHandler.computeSelection(layer);
                }

                resolve(layer);
            } else if (style.styleType == styleTypes.STYLE_TYPE_HEATMAP) {
                addOption(layerOptions, "gradient", style.style.ColorRamp);
                addOption(layerOptions, "radius", style.style.Radius);
                addOption(layerOptions, "blur", style.style.Blur);
                addOption(layerOptions, "weight", this._handleWeightFunction(style.style.IdEntityField, style.style.DefaultWeight));

                layer = new Heatmap(layerOptions);
                this.layers.push(layer);
                this.map.addLayer(layer);

                resolve(layer);
            } else if (style.styleType == styleTypes.STYLE_TYPE_HOTSPOT) {
                layer = createHotspotLayer({
                    source: layerOptions.source,
                    bandwidth: style.style.Bandwidth,
                    threshold: style.style.Threshold,
                    symbolSize: style.style.SymbolSize,
                    hotColor: style.style.HotColor,
                    coldColor: style.style.ColdColor,
                    neutralColor: style.style.NeutralColor,
                    spatialWeight: style.style.SpatialWeight,
                    weight: this._handleWeightFunction(style.style.IdEntityField, style.style.DefaultWeight),
                    zIndex: layerOptions.zIndex,
                    visible: layerOptions.visible
                });
                layer.set('uid', layerOptions.uid);
                this.layers.push(layer);
                this.map.addLayer(layer);

                if (layerSettings.IdHighlightStyle != null) {
                    this.selectionHandler.computeHover(layer);
                }

                if (layerSettings.IdSelectStyle != null) {
                    this.selectionHandler.computeSelection(layer);
                }

                resolve(layer);
            } else if (style.styleType == styleTypes.STYLE_TYPE_THEMATIC) {
                let themeStyle = style;

                // acelasi resolver ca prima cale: scanare client / range / clasificare (EQUAL/cuantile/jenks/stddev)
                this._handleTheme(layerSettings, style, layerOptions.source).then(resolved => {
                    if (resolved == null) {
                        resolve();
                        return;
                    }

                    // clasificarea calculata (per-layer) pentru legenda
                    layerSettings.thematicClassification = resolved.classificationIntervals || null;

                    if (resolved.styleType == styleTypes.STYLE_TYPE_CANVAS) {
                        let themeStyleGetter = createOlStyleFunction(resolved.style, this.map, this._getRenderSimplificationOptions());
                        let styleFunction = (feature) => {
                            return this._themeStyleGetter(feature, resolved.style, themeStyle, themeStyleGetter);
                        }

                        addOption(layerOptions, "style", styleFunction);
                        addOption(layerOptions, 'imageRatio', this._getConfiguredImageRatio());

                        layer = new VectorImageLayer(layerOptions);
                        this.layers.push(layer);
                        this.map.addLayer(layer);

                        this._attachThematicRecompute(layer, layerSettings, themeStyle, layerOptions.source);

                        if (layerSettings.IdHighlightStyle != null) {
                            this.selectionHandler.computeHover(layer);
                        }

                        if (layerSettings.IdSelectStyle != null) {
                            this.selectionHandler.computeSelection(layer);
                        }
                    } else {
                        addOption(layerOptions, 'style', resolved.style.featureStyle);
                        addOption(layerOptions, 'className', 'webgllayer');
                        layer = new WebGLLayer(layerOptions);
                        this.layers.push(layer);
                        this.map.addLayer(layer);

                        if (layerSettings.IdHighlightStyle != null) {
                            this.selectionHandler.computeHover(layer);
                        }

                        if (layerSettings.IdSelectStyle != null) {
                            this.selectionHandler.computeSelection(layer);
                        }
                    }

                    // daca legenda e deja deschisa, o notificam sa reflecte clasificarea calculata
                    $event.notify(eventTypes.THEMATIC_CLASSIFICATION_UPDATED, { layerId: layerSettings.Id }, this, this.classId);

                    resolve(layer);
                });
            }
            else if (style.styleType == 'CUSTOM' && window.CustomStyleHandler) {
                if (this.customStyleHandler == null) {
                    this.customStyleHandler = new CustomStyleHandler(this.styleHandler);
                }

                // Bin-urile descarca tot layerul (fara bbox); construim async, nu tinem harta dupa ele.
                let binPromise = this.customStyleHandler.handleCustomStyleLayer(layerSettings.IdDefaultStyle, layerSettings.IdHighlightStyle, layerSettings.IdSelectStyle, layerSettings, layerOptions, sourceOptions, this.map, this.layers, this.projectionHandler);
                if (this.mapControl && this.mapControl._profileLoadStep) {
                    this.mapControl._profileLoadStep('bins: ' + (layerSettings.Name || layerSettings.Id), binPromise);
                }
                resolve(layer);
            }
            else {
                addOption(layerOptions, 'style', style.style.featureStyle);
                addOption(layerOptions, 'className', 'webgllayer');
                layer = new WebGLLayer(layerOptions);
                this.layers.push(layer);
                this.map.addLayer(layer);

                if (layerSettings.IdHighlightStyle != null) {
                    this.selectionHandler.computeHover(layer);
                }

                if (layerSettings.IdSelectStyle != null) {
                    this.selectionHandler.computeSelection(layer);
                }

                if (style.style.isTimeDependant) {
                    this._handleLayerTimeDependency(layer.get('uid'));
                }

                resolve(layer);
            }
        });
    }

    _handleLayerTimeDependency(layerId) {
        let layers = this.getLayerById(layerId);
        if (layers.length > 0) {
            let layer = layers[0];

            let features = layer.getSource().getFeatures();
            let currentTime = Math.floor(new Date().getTime() / 1000);
            if (features.length > 0) {
                features.forEach((feature) => {
                    feature.set('currentTime', currentTime);
                });
            }
        }
        return requestAnimationFrame(() => { this._handleLayerTimeDependency(layerId) });
    }

    _createGeoserviceLayer(layer, idx) {
        switch (layer.Geoservice.ServiceType) {
            case layerTypes.OSM: return this._createOsmLayer(layer, idx);
            case layerTypes.WMS: return this._createGeoserviceWmsLayer(layer, idx);
            case layerTypes.WFS: return this._createGeoserviceWfsLayer(layer, idx);
            case layerTypes.GEOJSON: return this._createGeoserviceGeojsonLayer(layer, idx);
            case layerTypes.URL: return this._createGeoserviceUrlLayer(layer, idx);
            case layerTypes.WMTS: return this._createGeoserviceWmtsLayer(layer, idx);
            default: return new Promise((resolve, reject) => { resolve(); });
        }
    }

    _createGeoserviceWmtsLayer(layerSettings, idx) {
        return new Promise((resolve, reject) => {
            let url = layerSettings.Geoservice.Url;

            if (url.indexOf('http') != 0) {
                url = this.rootURL
                    + paths.PROXY_URL
                    + this.mapId
                    + '/'
                    + layerSettings.Fid.replaceAll('-', '')
                    + layerSettings.Geoservice.Url;
            }

            fetch(url, { mode: 'cors', credentials: 'omit' })
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`WMTS GetCapabilities failed: ${response.status} ${response.statusText}`);
                    }
                    return response.text();
                })
                .then(responseText => {
                    if (!responseText) {
                        // no capabilities returned
                        resolve(null);
                        return;
                    }

                    const result = (new WMTSCapabilities()).read(responseText);
                    const options = optionsFromCapabilities(result, {
                        layer: layerSettings.Geoservice.LayerName,
                        matrixSet: layerSettings.Geoservice.TileMatrix,
                        format: layerSettings.Geoservice.Format,
                        crossOrigin: 'anonymous',
                        transition: 0,
                        tilePixelRatio: 1
                    });

                    const source = new WMTS(options);
                    this.tileSources.push(source);

                    const layerOptions = {};
                    addOption(layerOptions, "uid", layerSettings.Id);
                    addOption(layerOptions, "source", source);
                    addOption(layerOptions, "visible", layerSettings.VisibleByDefault);
                    addOption(layerOptions, "preload", Infinity);
                    addOption(layerOptions, "zIndex", idx);
                    addOption(layerOptions, "originalZIndex", idx);
                    addOption(layerOptions, "extent", this._getLayerExtent(layerSettings));
                    addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, defaults.DEFAULT_SRID));
                    addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, defaults.DEFAULT_SRID));
                    addOption(layerOptions, "interpretation", layerTypes.WMTS);
                    addOption(layerOptions, "loadTilesWhileAnimating", true);
                    addOption(layerOptions, "loadTilesWhileInteracting", true);

                    const layer = new TileLayer(layerOptions);
                    this.layers.push(layer);
                    this.map.addLayer(layer);

                    resolve();
                })
                .catch(err => {
                    console.error('Error loading WMTS capabilities:', err);
                    resolve();
                });
        });
    }

    _createOsmLayer(layerSettings, idx) {
        return new Promise((resolve, reject) => {
            var layerOptions = {};
            addOption(layerOptions, 'uid', layerSettings.Id);

            let source = new OSM({
                transition: 0,
                tilePixelRatio: 1
            });

            this.tileSources.push(source);

            addOption(layerOptions, 'source', source);
            addOption(layerOptions, 'visible', layerSettings.VisibleByDefault);
            addOption(layerOptions, 'preload', Infinity);
            addOption(layerOptions, 'zIndex', idx);
            addOption(layerOptions, 'extent', this._getLayerExtent(layerSettings));
            addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, defaults.DEFAULT_SRID));
            addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, defaults.DEFAULT_SRID));
            addOption(layerOptions, 'interpretation', layerTypes.WMTS);
            addOption(layerOptions, "loadTilesWhileAnimating", true);
            addOption(layerOptions, "loadTilesWhileInteracting", true);
            let layer = new TileLayer(layerOptions)
            this.layers.push(layer);
            this.map.addLayer(layer);
            resolve();
        });
    }

    _createGeoserviceUrlLayer(layerSettings, idx) {
        return new Promise((resolve, reject) => {
            var layerOptions = {};
            addOption(layerOptions, 'uid', layerSettings.Id);

            let url = layerSettings.Geoservice.Url;

            if (url.indexOf('http') != 0) {
                url = this.rootURL + paths.PROXY_URL + this.mapId + '/' + layerSettings.Fid.replaceAll('-', '') + layerSettings.Geoservice.Url;
            }

            let source = new ImageTile({
                url: url,
                transition: 0,
                tilePixelRatio: 1
            });

            this.tileSources.push(source);

            addOption(layerOptions, 'source', source);
            addOption(layerOptions, 'visible', layerSettings.VisibleByDefault);
            addOption(layerOptions, 'preload', Infinity);
            addOption(layerOptions, 'zIndex', idx);
            addOption(layerOptions, 'extent', this._getLayerExtent(layerSettings));
            addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, defaults.DEFAULT_SRID));
            addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, defaults.DEFAULT_SRID));
            addOption(layerOptions, 'interpretation', layerTypes.WMTS);
            addOption(layerOptions, "loadTilesWhileAnimating", true);
            addOption(layerOptions, "loadTilesWhileInteracting", true);
            let layer = new TileLayer(layerOptions)
            this.layers.push(layer);
            this.map.addLayer(layer);
            resolve();
        });
    }

    _createGeoserviceWmsLayer(layerSettings, idx) {
        return new Promise((resolve, reject) => {
            this.projectionHandler.load(layerSettings.Geoservice.TileMatrix).then(() => {
                var sourceOptions = {};
                var params = {};

                let url = layerSettings.Geoservice.RequestUrl.replaceAll('SERVICE=WMS&', '');

                if (url.indexOf('http') != 0) {
                    url = this.rootURL + paths.PROXY_URL + this.mapId + '/' + layerSettings.Fid.replaceAll('-', '') + layerSettings.Geoservice.RequestUrl.replaceAll('SERVICE=WMS&', '');
                }

                addOption(params, 'LAYERS', layerSettings.Geoservice.LayerName);
                addOption(params, 'FORMAT', layerSettings.Geoservice.Format == null ? formats.DEFAULT_WMS_FORMAT : layerSettings.Geoservice.Format);
                addOption(params, 'VERSION', layerSettings.Geoservice.Version);
                addOption(sourceOptions, 'params', params);
                addOption(sourceOptions, 'url', url);
                addOption(sourceOptions, 'projection', this.projectionHandler.getProj(layerSettings.Geoservice.TileMatrix));
                addOption(sourceOptions, 'ratio', defaults.WMS_RATIO);
                addOption(sourceOptions, 'crossOrigin', defaults.CROSS_ORIGIN);

                var source = new ImageWMS(sourceOptions);

                var layerOptions = {};
                addOption(layerOptions, 'uid', layerSettings.Id);
                addOption(layerOptions, 'source', source);
                addOption(layerOptions, 'visible', layerSettings.VisibleByDefault);
                addOption(layerOptions, 'extent', this._getLayerExtent(layerSettings));
                addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, layerSettings.Geoservice.TileMatrix));
                addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, layerSettings.Geoservice.TileMatrix));
                addOption(layerOptions, 'zIndex', idx);
                addOption(layerOptions, "interpretation", layerTypes.WMS);

                let layer = new ImageLayer(layerOptions);
                this.layers.push(layer);
                this.map.addLayer(layer);

                resolve();
            });
        });
    }

    _createGeoserviceGeojsonLayer(layerSettings, idx) {
        return new Promise((resolve, reject) => {
            this.projectionHandler.load(layerSettings.Geoservice.TileMatrix).then(() => {
                this.styleHandler.loadStyle((layerSettings.IdDefaultStyle == null ? 'sys_' + styleTypes.SYSTEM_STYLE_ALL : layerSettings.IdDefaultStyle), (layerSettings.IdDefaultStyle == null ? true : false)).then(() => {
                    this.styleHandler.loadStyle(layerSettings.IdHighlightStyle).then(() => {
                        this.styleHandler.loadStyle(layerSettings.IdSelectStyle).then(() => {
                            var sourceOptions = {};

                            var formatOptions = {};

                            formatOptions.layerId = layerSettings.Id;
                            formatOptions.selectionHandler = this.selectionHandler;
                            formatOptions.generalActionHandler = this.mapControl.generalActionHandler;
                            formatOptions.layerSettings = layerSettings;

                            if (layerSettings.Geoservice.GeometryColumn != null && layerSettings.Geoservice.GeometryColumn != '') {
                                formatOptions.geometryName = layerSettings.Geoservice.GeometryColumn;
                            }

                            var geoJsonFormat = new XGeoJSON(formatOptions);

                            addOption(sourceOptions, 'format', geoJsonFormat);
                            addOption(sourceOptions, 'url', handleGeoserviceGeojsonWfsUrl(this.mapId, this.rootURL, layerSettings, this.map));

                            addOption(sourceOptions, 'loader', xhr(sourceOptions['url'], sourceOptions['format']));

                            addOption(sourceOptions, 'strategy', allStrategy);
                            var source = new VectorSource(sourceOptions);

                            var layerOptions = {};

                            addOption(layerOptions, "uid", layerSettings.Id);
                            addOption(layerOptions, "source", source);

                            addOption(layerOptions, 'visible', layerSettings.VisibleByDefault);
                            addOption(layerOptions, 'extent', this._getLayerExtent(layerSettings));
                            addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, layerSettings.Geoservice.TileMatrix));
                            addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, layerSettings.Geoservice.TileMatrix));
                            addOption(layerOptions, "declutter", layerSettings.Declutter);
                            addOption(layerOptions, "zIndex", idx);
                            addOption(layerOptions, "interpretation", layerTypes.WFS);
                            addOption(layerOptions, 'idField', layerSettings.Geoservice.IdColumn);

                            this._createVectorLayer(layerSettings, layerOptions, sourceOptions).then(() => { resolve(); });
                        });
                    });
                });
            });
        });
    }

    _createGeoserviceWfsLayer(layerSettings, idx) {
        return new Promise((resolve, reject) => {
            this.projectionHandler.load(layerSettings.Geoservice.TileMatrix).then(() => {
                this.styleHandler.loadStyle((layerSettings.IdDefaultStyle == null ? 'sys_' + styleTypes.SYSTEM_STYLE_ALL : layerSettings.IdDefaultStyle), (layerSettings.IdDefaultStyle == null ? true : false)).then(() => {
                    this.styleHandler.loadStyle(layerSettings.IdHighlightStyle).then(() => {
                        this.styleHandler.loadStyle(layerSettings.IdSelectStyle).then(() => {
                            var sourceOptions = {};

                            var formatOptions = {};

                            formatOptions.layerId = layerSettings.Id;
                            formatOptions.selectionHandler = this.selectionHandler;
                            formatOptions.generalActionHandler = this.mapControl.generalActionHandler;
                            formatOptions.layerSettings = layerSettings;

                            if (layerSettings.Geoservice.GeometryColumn != null && layerSettings.Geoservice.GeometryColumn != '') {
                                formatOptions.geometryName = layerSettings.Geoservice.GeometryColumn;
                            }

                            var geoJsonFormat = new XGeoJSON(formatOptions);

                            addOption(sourceOptions, 'format', geoJsonFormat);
                            addOption(sourceOptions, 'url', handleGeoserviceWfsUrl(this.mapId, this.rootURL, layerSettings, this.map));

                            addOption(sourceOptions, 'loader', xhr(sourceOptions['url'], sourceOptions['format']));

                            let loadingStrategy = layerSettings.LoadingStrategy == null || layerSettings.LoadingStrategy == '' ? defaults.WFS_DEFAULT_LOADING_STRATEGY : layerSettings.LoadingStrategy;

                            switch (loadingStrategy) {
                                case 'bboxStrategy':
                                    addOption(sourceOptions, 'strategy', bboxStrategy);
                                    break;
                                case "allStrategy":
                                    addOption(sourceOptions, 'strategy', allStrategy);
                                    break;
                            }
                            var source = new VectorSource(sourceOptions);


                            var layerOptions = {};

                            addOption(layerOptions, "uid", layerSettings.Id);
                            addOption(layerOptions, "source", source);

                            addOption(layerOptions, 'visible', layerSettings.VisibleByDefault);
                            addOption(layerOptions, 'extent', this._getLayerExtent(layerSettings));
                            addOption(layerOptions, 'minResolution', getResolutionForScale(layerSettings.MinScale, layerSettings.Geoservice.TileMatrix));
                            addOption(layerOptions, 'maxResolution', getResolutionForScale(layerSettings.MaxScale, layerSettings.Geoservice.TileMatrix));
                            addOption(layerOptions, "declutter", layerSettings.Declutter);
                            addOption(layerOptions, "zIndex", idx);
                            addOption(layerOptions, "interpretation", layerTypes.WFS);
                            addOption(layerOptions, 'idField', layerSettings.Geoservice.IdColumn);

                            this._createVectorLayer(layerSettings, layerOptions, sourceOptions).then(() => { resolve(); });
                        });
                    });
                });
            });
        });
    }

    _createTemporaryLayer(options) {
        if (options.parentId != null && this.layerSettingsHandler.getLayerSettings(options.parentId) == null && options.addToLegend) {
            this.layerSettingsHandler.addLayer({
                Type: layerTypes.GROUP,
                Id: options.parentId,
                Title: options.parentDisplayName,
                VisibleByDefault: options.visible != null ? options.visible : true,
                Items: []
            }, 0, null);
        }

        return new Promise((resolve, reject) => {
            this.projectionHandler.load(options.srid).then(() => {
                let zIndex = this.temporaryLayerIdx;

                if (options.addToLegend) {
                    if (options.zIndex != null) {
                        zIndex = options.zIndex;
                    } else {
                        zIndex = this.layerIdx;
                        this.layerIdx++;
                    }
                } else {
                    this.temporaryLayerIdx++;
                }

                let sourceOptions = {};
                addOption(sourceOptions, 'strategy', allStrategy);

                let source = new VectorSource(sourceOptions);

                let layerOptions = {};

                addOption(layerOptions, "uid", options.name);
                addOption(layerOptions, "source", source);

                addOption(layerOptions, 'visible', options.visible != null ? options.visible : true);
                addOption(layerOptions, "declutter", options.declutter != null ? options.declutter : true);
                addOption(layerOptions, "zIndex", zIndex);
                addOption(layerOptions, "interpretation", layerTypes.WFS);
                addOption(layerOptions, 'idField', options.idField);
                addOption(layerOptions, 'temporarySettings', options);

                if (options.ignoreStyle) {
                    let layer = new VectorImageLayer(layerOptions);
                    this.layers.push(layer);
                    this.map.addLayer(layer);

                    if (options.addToLegend) {
                        this.layerSettingsHandler.addLayer(options, 0, options.parentId, options.showLegend != null ? !options.showLegend : false);
                    }

                    if (options.selectable) {
                        this.selectionHandler.computeHover(layer, {
                            Temporary: {
                                IdColumn: options.idField,
                                Tooltip: options.Tooltip
                            },
                            Selectable: options.Selectable
                        });

                        this.selectionHandler.computeSelection(layer, {
                            Temporary: {
                                IdColumn: options.idField,
                                Tooltip: options.Tooltip
                            },
                            Selectable: options.Selectable
                        });
                    }

                    resolve({
                        status: true
                    });
                } else {
                    this.styleHandler.loadStyle(options.style, options.isSystemStyle).then(() => {
                        this.styleHandler.loadStyle(options.highlightStyle, options.isSystemHighlightStyle).then(() => {
                            this.styleHandler.loadStyle(options.selectStyle, options.isSystemSelectStyle).then(() => {
                                let style = this.styleHandler.getCombinedStyle(options.style, options.highlightStyle, options.selectStyle);

                                let layer;

                                //here

                                if (style.styleType == styleTypes.STYLE_TYPE_CANVAS) {
                                    let styleGetter = createOlStyleFunction(style.style, this.map, this._getRenderSimplificationOptions());
                                    let styleFunction = (feature) => {
                                        return styleGetter(feature);
                                    }

                                    addOption(layerOptions, "style", styleFunction);

                                    layer = new VectorImageLayer(layerOptions);
                                    this.layers.push(layer);
                                    this.map.addLayer(layer);
                                } else if (style.styleType == styleTypes.STYLE_TYPE_HEATMAP) {
                                    addOption(layerOptions, "gradient", style.style.ColorRamp);
                                    addOption(layerOptions, "radius", style.style.Radius);
                                    addOption(layerOptions, "blur", style.style.Blur);
                                    addOption(layerOptions, "weight", this._handleWeightFunction(style.style.IdEntityField, style.style.DefaultWeight));

                                    layer = new Heatmap(layerOptions);
                                    this.layers.push(layer);
                                    this.map.addLayer(layer);
                                } else if (style.styleType == styleTypes.STYLE_TYPE_HOTSPOT) {
                                    layer = createHotspotLayer({
                                        source: layerOptions.source,
                                        bandwidth: style.style.Bandwidth,
                                        threshold: style.style.Threshold,
                                        symbolSize: style.style.SymbolSize,
                                        hotColor: style.style.HotColor,
                                        coldColor: style.style.ColdColor,
                                        neutralColor: style.style.NeutralColor,
                                        spatialWeight: style.style.SpatialWeight,
                                        weight: this._handleWeightFunction(style.style.IdEntityField, style.style.DefaultWeight),
                                        zIndex: layerOptions.zIndex,
                                        visible: layerOptions.visible
                                    });
                                    layer.set('uid', layerOptions.uid);
                                    this.layers.push(layer);
                                    this.map.addLayer(layer);
                                } else {
                                    addOption(layerOptions, 'style', style.style.featureStyle);
                                    addOption(layerOptions, 'className', 'webgllayer');
                                    layer = new WebGLLayer(layerOptions);
                                    this.layers.push(layer);
                                    this.map.addLayer(layer);
                                }

                                if (options.highlightStyle != null) {
                                    this.selectionHandler.computeHover(layer, {
                                        Temporary: {
                                            IdColumn: options.idField,
                                            Tooltip: options.Tooltip
                                        },
                                        Selectable: options.Selectable
                                    });
                                }

                                if (options.selectStyle != null) {
                                    this.selectionHandler.computeSelection(layer, {
                                        Temporary: {
                                            IdColumn: options.idField,
                                            Tooltip: options.Tooltip
                                        },
                                        Selectable: options.Selectable
                                    });
                                }

                                if (options.addToLegend) {
                                    this.layerSettingsHandler.addLayer(options, 0, options.parentId, options.showLegend != null ? !options.showLegend : false);
                                }

                                resolve({
                                    status: true
                                });
                            });
                        });
                    });
                }
            });
        });
    }

    _handleWeightFunction(fieldName, defaultWeight) {
        var fallback = defaultWeight != null && !isNaN(Number(defaultWeight))
            ? Number(defaultWeight)
            : 0.3;

        if (fieldName != null && typeof fieldName === 'string' && fieldName.trim().length > 0) {
            var field = fieldName;
            return function (feature) {
                var v = feature.get(field);
                return v != null ? v : fallback;
            };
        }

        return function (feature) { return fallback; };
    }

    _checkTemporaryVector(options) {
        let result = {
            message: null,
            status: true
        };

        if (options.name == null) {
            result.status = false;
            result.message = 'name is mandatory';
            return result;
        }

        if (this.getLayerById(options.name).length > 0) {
            result.status = false;
            result.message = 'Layer already exists';
            return result;
        }

        if (options.geometryType == null || options.geometryType.trim() == '') {
            result.status = false;
            result.message = 'geometryType is mandatory';
            return result;
        }

        if (options.srid == null || options.srid.trim() == '') {
            result.status = false;
            result.message = 'srid is mandatory';
            return result;
        }

        if (options.geomField == null || options.geomField.trim() == '') {
            result.status = false;
            result.message = 'geomField is mandatory';
            return result;
        }

        if (options.idField == null || options.idField.trim() == '') {
            result.status = false;
            result.message = 'idField is mandatory';
            return result;
        }

        if (options.style == null || options.style.trim() == '') {
            options.isSystemStyle = true;
            switch (options.geometryType) {
                case geomTypes.GEOM_POINT: options.style = 'sys_' + styleTypes.SYSTEM_STYLE_POINT;
                    break;
                case geomTypes.GEOM_LINE: options.style = 'sys_' + styleTypes.SYSTEM_STYLE_LINE;
                    break;
                case geomTypes.GEOM_POLYGON: options.style = 'sys_' + styleTypes.SYSTEM_STYLE_POLYGON;
                    break;
                default: options.style = 'sys_' + styleTypes.SYSTEM_STYLE_ALL;
                    break;
            }
        }

        if ((options.highlightStyle == null || options.highlightStyle.trim() == '') && options.isSystemStyle) {
            options.isSystemHighlightStyle = true;
            switch (options.geometryType) {
                case geomTypes.GEOM_POINT: options.highlightStyle = 'sys_' + styleTypes.SYSTEM_STYLE_HOVER_POINT;
                    break;
                case geomTypes.GEOM_LINE: options.highlightStyle = 'sys_' + styleTypes.SYSTEM_STYLE_HOVER_LINE;
                    break;
                case geomTypes.GEOM_POLYGON: options.highlightStyle = 'sys_' + styleTypes.SYSTEM_STYLE_HOVER_POLYGON;
                    break;
                default: options.highlightStyle = 'sys_' + styleTypes.SYSTEM_STYLE_HOVER_ALL;
                    break;
            }
        }

        if ((options.selectStyle == null || options.selectStyle.trim() == '') && options.isSystemStyle) {
            options.isSystemSelectStyle = true;
            switch (options.geometryType) {
                case geomTypes.GEOM_POINT: options.selectStyle = 'sys_' + styleTypes.SYSTEM_STYLE_SELECT_POINT;
                    break;
                case geomTypes.GEOM_LINE: options.selectStyle = 'sys_' + styleTypes.SYSTEM_STYLE_SELECT_LINE;
                    break;
                case geomTypes.GEOM_POLYGON: options.selectStyle = 'sys_' + styleTypes.SYSTEM_STYLE_SELECT_POLYGON;
                    break;
                default: options.selectStyle = 'sys_' + styleTypes.SYSTEM_STYLE_SELECT_ALL;
                    break;
            }
        }

        if (options.minFitScale == null) {
            switch (options.geometryType) {
                case geomTypes.GEOM_POINT: options.minFitScale = 1500;
                    break;
                case geomTypes.GEOM_LINE: options.minFitScale = 2500;
                    break;
                case geomTypes.GEOM_POLYGON: options.minFitScale = 5000;
                    break;
                default: options.minFitScale = 5000;
                    break;
            }
        }

        options.Type = layerTypes.TEMPORARY_VECTOR;
        options.VisibleByDefault = options.visible != null ? options.visible : true;
        options.Id = options.name;
        options.Opacity = 1.0;
        options.Title = options.displayName;
        options.IdDefaultStyle = options.style;
        options.Interpretation = layerTypes.WFS;

        if (options.highlightStyle != null) {
            options.IdHighlightStyle = options.highlightStyle;
            options.Selectable = true;
        }

        if (options.selectStyle != null) {
            options.IdSelectStyle = options.selectStyle;
            options.Selectable = true;
        }

        if (options.selectable) {
            options.Selectable = true;
        }

        options.Tooltip = options.tooltip;

        return result;
    }

    _getLayerExtent(layer) {
        let xMin = layer.XMin;
        let yMin = layer.YMin;
        let xMax = layer.XMax;
        let yMax = layer.YMax;

        if (xMin != null && yMin != null && xMax != null && yMax != null) {
            return [xMin, yMin, xMax, yMax];
        }

        return this.projectionHandler.getProjExtent(this.generalSettingsHandler.settings.ProjCode);
    }

    _createGraticuleLayerFromConfig(layerSettings, idx) {
        return new Promise((resolve) => {
            this._buildProjectedGridLayer(
                idx,
                layerSettings.VisibleByDefault,
                layerSettings.Opacity == null ? 1 : layerSettings.Opacity,
                layerSettings.Id
            );
            resolve();
        });
    }

    _buildProjectedGridLayer(idx, visible, opacity, uid) {
        //CG: loader + strategy => grila se reface pentru frame-ul care chiar se deseneaza
        //CG: (inclusiv cel de print), nu pentru extentul de la ultimul moveend.
        let loadedExtent = null;
        let source = new VectorSource({
            strategy: (extent, resolution) => {
                if (loadedExtent != null) {
                    if (approximatelyEquals(loadedExtent, extent, resolution)) {
                        return [loadedExtent.slice()];
                    }

                    source.removeLoadedExtent(loadedExtent);
                }

                return [extent];
            },
            loader: (extent, resolution, projection) => {
                loadedExtent = extent;
                this._updateProjectedGrid(source, extent, resolution, projection);
            }
        });
        let lineStyle = new Style({
            stroke: new Stroke({ color: 'rgba(0,0,0,0.6)', width: 1, lineDash: [4, 4] })
        });
        let labelFill = new Fill({ color: 'rgba(0,0,0,0.85)' });
        let labelStroke = new Stroke({ color: 'rgba(255,255,255,0.85)', width: 2 });

        let gridLayer = new VectorLayer({
            source: source,
            zIndex: idx,
            visible: visible === true,
            //CG: fara renderBuffer, altfel loaderul primeste extentul umflat cu 100px si
            //CG: etichetele ar cadea in afara cadrului; in plus forteaza refacerea la orice pan
            renderBuffer: 0,
            style: (feature) => {
                if (feature.get('kind') === 'line') return lineStyle;
                return new Style({
                    text: new Text({
                        text: feature.get('text'),
                        font: '11px sans-serif',
                        fill: labelFill,
                        stroke: labelStroke,
                        overflow: true,
                        textAlign: feature.get('textAlign') || 'center',
                        textBaseline: feature.get('textBaseline') || 'middle'
                    })
                });
            }
        });
        gridLayer.setOpacity(opacity == null ? 1 : opacity);
        gridLayer.set('uid', uid);
        this.layers.push(gridLayer);
        this.map.addLayer(gridLayer);

        return gridLayer;
    }

    _updateProjectedGrid(source, extent, resolution, projection) {
        source.clear();
        if (extent == null || !(resolution > 0)) return;
        let isDegrees = projection.getUnits() === 'degrees';

        let steps = isDegrees
            ? [0.00001, 0.00002, 0.00005, 0.0001, 0.0002, 0.0005,
               0.001, 0.002, 0.005, 0.01, 0.02, 0.05,
               0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 45, 90]
            : [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000,
               10000, 20000, 50000, 100000, 200000, 500000, 1000000,
               2000000, 5000000, 10000000];

        //CG: pasul se leaga de scara (spatiere tinta in pixeli), nu de latimea ferestrei
        let desired = defaults.GRATICULE_TARGET_SPACING_PX * resolution;
        let step = steps[steps.length - 1];
        for (let i = 0; i < steps.length; i++) {
            if (steps[i] >= desired) { step = steps[i]; break; }
        }

        let iStart = Math.floor(extent[0] / step);
        let jStart = Math.floor(extent[1] / step);
        let iEnd = Math.ceil(extent[2] / step);
        let jEnd = Math.ceil(extent[3] / step);
        let x0 = iStart * step;
        let y0 = jStart * step;
        let x1 = iEnd * step;
        let y1 = jEnd * step;
        //CG: etichetele stau retrase in interiorul cadrului desenat, pe toate 4 laturile,
        //CG: ca sa ramana vizibile si la print (unde cadrul e alt dreptunghi decat ecranul).
        let inset = defaults.GRATICULE_LABEL_INSET_PX * resolution;

        let features = [];
        let addLabel = (coordinate, text, textAlign, textBaseline) => {
            let label = new Feature({ geometry: new Point(coordinate) });
            label.set('kind', 'label');
            label.set('text', text);
            label.set('textAlign', textAlign);
            label.set('textBaseline', textBaseline);
            features.push(label);
        };

        for (let i = iStart; i <= iEnd; i++) {
            let x = i * step;
            let line = new Feature({ geometry: new LineString([[x, y0], [x, y1]]) });
            line.set('kind', 'line');
            features.push(line);

            //CG: liniile de capat cad in afara cadrului; le desenam, dar nu le etichetam
            if (x < extent[0] || x > extent[2]) continue;

            let text = this._formatGridValue(x, step, isDegrees);
            addLabel([x, extent[1] + inset], text, 'center', 'bottom');
            addLabel([x, extent[3] - inset], text, 'center', 'top');
        }
        for (let j = jStart; j <= jEnd; j++) {
            let y = j * step;
            let line = new Feature({ geometry: new LineString([[x0, y], [x1, y]]) });
            line.set('kind', 'line');
            features.push(line);

            if (y < extent[1] || y > extent[3]) continue;

            let text = this._formatGridValue(y, step, isDegrees);
            addLabel([extent[0] + inset, y], text, 'left', 'middle');
            addLabel([extent[2] - inset, y], text, 'right', 'middle');
        }
        source.addFeatures(features);
    }

    _formatGridValue(value, step, isDegrees) {
        if (isDegrees) {
            let precision = 0;
            if (step < 1) precision = 1;
            if (step < 0.1) precision = 2;
            if (step < 0.01) precision = 3;
            if (step < 0.001) precision = 4;
            if (step < 0.0001) precision = 5;
            return value.toFixed(precision) + '°';
        }
        //CG: caroiajul proiectat se eticheteaza mereu in metri, fara salt la km pe zoom
        return Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' m';
    }
}
